import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffTwoTone';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnTwoTone';
// @ts-ignore
import { DragDropContext, DropResult, Droppable, DroppableProvided } from '@nurba94/react-beautiful-dnd-custom-nurba';
import PlaybackSpeed from '@mui/icons-material/ScheduleOutlined';
import { isHotkeyPressed, useHotkeys } from 'react-hotkeys-hook';
import UndoIcon from '@mui/icons-material/KeyboardBackspace';
import MuteIcon from '@mui/icons-material/VolumeMuteRounded';
import VolumeIcon from '@mui/icons-material/VolumeUpRounded';
import { useSnackbar } from 'material-ui-snackbar-provider';
import CenterLine from '@mui/icons-material/HorizontalRule';
import AddClipIcon from '@mui/icons-material/AddRounded';
import FullScreen from '@mui/icons-material/ZoomOutMap';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Locate from '@mui/icons-material/MyLocation';
import { Box, Slider, Stack, Popper } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import { useOnClickOutside } from 'usehooks-ts';
import { enqueueSnackbar } from 'notistack';
import { ChromePicker } from 'react-color';
import { arrayMove } from 'react-movable';
import Draggable from 'react-draggable';
import lodash from 'lodash';
import cx from 'classnames';

import { useClipFavourites, useFavouriteEditor } from '../ClipFavourites/context';
import { useSidebarPlayback } from '../ClipFavourites/useSidebarPlayback';
import { FavouriteStar } from '../ClipFavourites/Star';
import { useClipFilter } from '../ClipFilter/context';
import { FilterToolbarButton, FilterSummary } from '../ClipFilter/Panel';
import { openDownloadManager } from '../../store/downloadManager/downloadManagerSlice';
import {
  setShowSpeedOption,
  toFollowPlayhead,
  triggerRefetch,
  updateSecondaySelectedClips,
  updateOrderedSecondarySelectedClips,
  updateShowNotes,
  updateShowWarningModal,
} from '../../store/sharedStore/sharedStoreSlice';
import { CLIP, COLLABORATIVE_FOLDER_TYPE, NOTIFICATION, TOPIC, VIDEOFEED, VIDEOFEED_TYPE, VIDEO_TYPE } from '../../enums';
import {
  deleteTopicInReflexComposer,
  editClip,
  setClipAngleLock,
  estimateComposerDownload,
  getFolderById,
  getGeneratedComposerClipIds,
  updateTopicName,
} from '../../api';
import DeleteComposerAdditional from './DeleteComposerClipModal/DeleteComposerAdditional';
import { ReactComponent as DownloadIcon } from '../../assets/icons/pure-download.svg';
import { deleteTopicInComposer, getComposerDataByVideoFeedId, patchReoderComposer } from '../../api/composersApi';
import { ReactComponent as SendIcon } from '../../assets/icons/sendClipsIcon.svg';
import { ReactComponent as ZoomOutIcon } from '../../assets/icons/zoom-out.svg';
import { ReactComponent as ZoomInIcon } from '../../assets/icons/zoom-in.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import DownloadDataModal from '../modules/VideoFileList/DownloadDataModal';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import {
  getComposerWebPreferences,
  parseColorToHex,
  rememberManualVolumeOverrideForFeed,
  subscribeComposerWebPreferences,
  timeLineFormatTime,
  useDimensions,
} from '../../helpers';
import { ContainedButton, OutlinedButton } from '../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import VideoQualitySelector from '../modules/VideoQualitySelector';
import DeleteComposerClipModal from './DeleteComposerClipModal';
import { findSearchValue } from '../../helpers/getSearchValue';
import { TimelineButton } from '../Timeline/TimelineButton';
import { useAutoRepeat } from '../../helpers/useAutoRepeat';
import ComposerRow, { COMPOSER_CLIP_RIGHT_MARGIN, TComposerClip } from './ComposerRow';
import { ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import CreateRowModal from '../Timeline/CreateRowModal';
import FullPageLoader from '../modules/FullPageLoader';
import LinearProgress from '../modules/LinearProgress';
import CopyLinkModal from '../Timeline/CopyLinkModal';
import SendClipModal from '../Timeline/SendClipModal';
import CreateOrSendModal from './CreateOrSendModal';
import NewComposerModal from './NewComposerModal';
import GeneratedMoviesWarningModal from '../modules/GeneratedMoviesWarningModal';
import MobileControlCenter from '../modules/MobileControlCenter';
import { TClip } from '../Timeline/TimelineClip';
import { RolesLowerCase } from '../../enums/ROLES';
import {
  clearComposerData,
  getAllComposersThunk,
  updateComposerPlayControls,
  updateComposerRows,
  updateComposerClipAngleLock,
  updateComposerVideoLength,
  saveCurrentComposerVideos,
  reorderComposerRows,
  getAllSharedComposerThunk,
  updateComposerRowSelection,
  updateCurrentSelectedComposerClips,
  updateCurrentSelectedComposerClipsKeepingRows,
  updateSelectedMultipleClips,
  updateCustomTime,
  setComposerMarkerPosition,
  triggerRefresh,
  updateAnglesCount,
  TSessionRows,
  updateCurrentComposerData,
  setFetchComments,
  updateMarkMargin,
  updateComposerReady,
} from '../../store/composers/composersSlice';
import { formatSeconds } from '../Timeline';
import styles from './Composer.module.scss';
import { checkContentAvailability } from '../../api/accessesApi';

export type UndoAction = { exec: (overrideTopicId?: number) => Promise<void> };

const CLIP_EDITOR_ZOOM_LEVELS = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 32, 40, 48, 64];
const BASE_MAX_CLIP_EDITOR_ZOOM = CLIP_EDITOR_ZOOM_LEVELS[CLIP_EDITOR_ZOOM_LEVELS.length - 1];
const ignoreSendClipShortcut = (event: KeyboardEvent) =>
  Boolean(
    document.getElementById('clip-drawer-modal') ||
      (event.target as HTMLElement | null)?.closest('[role="dialog"]'),
  );

const getClipEditorZoomStep = (zoom: number, direction: 'in' | 'out', maxZoom: number) => {
  if (direction === 'in') {
    const nextZoom = CLIP_EDITOR_ZOOM_LEVELS.find((level) => level > zoom + 0.001) ?? zoom * 1.2;
    return Math.min(nextZoom, maxZoom);
  }

  if (zoom > BASE_MAX_CLIP_EDITOR_ZOOM) return Math.max(BASE_MAX_CLIP_EDITOR_ZOOM, zoom / 1.2);
  for (let index = CLIP_EDITOR_ZOOM_LEVELS.length - 1; index >= 0; index -= 1) {
    if (CLIP_EDITOR_ZOOM_LEVELS[index] < zoom - 0.001) return CLIP_EDITOR_ZOOM_LEVELS[index];
  }
  return 1;
};

const CLIP_EDITOR_TICK_INTERVALS = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400];

export type TComposerFile = {
  name: string;
  order: number;
  timeline: {
    videofeed: {
      created_by: string;
      created_at: string;
    };
  };
  clips: TClip[];
};

export type TClipDrawer = {
  time_start: number;
  time_end: number;
  original_time_start?: number;
  original_time_end?: number;
  topicId: string;
  id?: string;
  color?: string;
  isEdit?: boolean;
} | null;

export const DROP_TYPE = {
  TOPIC: 'topic',
  CLIP: 'clip',
};

const getUniqueById = (array: TClip[], key: string) => {
  const seen: { [key: string]: boolean } = {};
  return array.filter((item) => {
    // @ts-ignore
    const itemId = item[key];
    if (!seen[itemId]) {
      seen[itemId] = true;
      return true;
    }
    return false;
  });
};

type TComposer = {
  videoPlayerHeight: number;
  fullScreen: any;
  videoUrl?: string;
  videoData?: VIDEO_TYPE;
  setDynamicZoom: (b: boolean) => void;
  dynamicZoom: boolean;
  computedQuality: string;
  onShowComments: () => void;
};

let previousFeed = '';
let autoZoomed = false;

// previous clip in visual order
function getPrevClipPosition(r: number, c: number, rows: TComposerClip[], dimmedIds: Set<string>) {
  for (let rr = r; rr >= 0; rr--) {
    const clips = rows[rr]?.clips || [];
    for (let cc = rr === r ? c - 1 : clips.length - 1; cc >= 0; cc--) {
      if (!dimmedIds.has(String(clips[cc].id))) return { rowIndex: rr, clipIndex: cc };
    }
  }
  return null;
}

// next/prev within the selected set (order = currentSelectedComposerClips as-is)
function getPrevSelectedClipId(currentId: string, selected: TClip[], dimmedIds: Set<string>) {
  for (let i = selected.findIndex((clip) => clip.id === currentId) - 1; i >= 0; i--) {
    if (!dimmedIds.has(String(selected[i].id))) return selected[i].id;
  }
  return null;
}

const Composer: React.FC<TComposer> = ({
  videoPlayerHeight,
  fullScreen,
  videoData,
  setDynamicZoom,
  dynamicZoom,
  computedQuality,
  onShowComments,
}) => {
  const PAUSE_OVERLAY_HIDE_EVENT = 'amplify:hide-pause-overlay';
  const notifyHidePauseOverlay = () => {
    window.dispatchEvent(new CustomEvent(PAUSE_OVERLAY_HIDE_EVENT, { detail: { context: 'composer' } }));
  };

  const dispatch = useAppDispatch();
  const { enterClip, setWorkspace } = useClipFavourites();
  const { config: clipFilterConfig, active: filterActive, dimmedIds, collapsedRowIds, shown, waitingForComments } = useClipFilter();
  const { widthD } = useDimensions();
  const isMobile = widthD < 768;
  const [isMobileControlCenterOpen, setIsMobileControlCenterOpen] = useState(true);
  const {
    composerRows,
    composerPlayControl,
    customTime,
    currentComposerVideos,
    composerVideoClips,
    progressPosition,
    currentSelectedComposerClips,
    currentSelectedComposerRows,
    lastSelectedComposerRowId,
    videoLength: composerSourceDuration,
    markMargin,
    rowWidth,
  } = useAppSelector((state) => state.composersStore);
  const { showNotes, followPlayhead, triggerForRefetching, showSpeedOption, emptyComposerStackActionTrigger, currentVideoThatInFrame } = useAppSelector(
    (state) => state.sharedStore,
  );
  const { videoLength } = useAppSelector((state) => state.videoStore);
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloadButtonHovered, setIsDownloadButtonHovered] = useState(false);
  const [userAccess, setUserAccess] = useState<ACCESS_TYPE_ENUM | 'idle'>('idle');
  const isContentAdminOA = findSearchValue('allContent') === 'true' && currentUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN;
  const [composerData, setComposerData] = useState<VIDEOFEED>();
  const hasFixedClipBounds = composerData?.type === VIDEOFEED_TYPE.COMPOSER;
  const canTrimClipToPlayhead = hasFixedClipBounds || composerData?.type === VIDEOFEED_TYPE.MY_COMPOSER;
  const [maxMarkMargin, setMaxMarkMargin] = useState<number>(1);
  const [dragging, setDragging] = useState<'left' | 'right' | 'center' | 'playhead' | null>(null);

  const handleMouseUp = () => {
    if (!dragging) return;
    clipEditorDragViewportRef.current = null;
    setDragging(null);
  };

  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    const r = composerPlayControl.currentRowIndex;
    const c = composerPlayControl.currentClipIndex;
    const clip = composerRows?.[r]?.clips?.[c];
    if (!clip) return;

    if (composerPlayControl.composerControls.playedSeconds !== 0) return;

    dispatch(updateCustomTime(clip.time_start));
    dispatch(setComposerMarkerPosition({ rowIndex: r, clipIndex: c }));
    dispatch(toFollowPlayhead(true));
  }, [composerPlayControl.currentRowIndex, composerPlayControl.currentClipIndex, composerPlayControl.composerControls.playedSeconds]);

  useEffect(() => {
    dispatch(updateMarkMargin(markMargin));
  }, [markMargin]);

  const feedId = findSearchValue('feedId');
  const acceptedGeneratedMoviesWarningRef = useRef<Partial<Record<string, boolean>>>({});
  const generatedMoviesWarningResolverRef = useRef<((accepted: boolean) => void) | null>(null);
  const [showGeneratedMoviesWarningModal, setShowGeneratedMoviesWarningModal] = useState(false);

  const resolveGeneratedMoviesWarningModal = useCallback((accepted: boolean) => {
    if (generatedMoviesWarningResolverRef.current) {
      generatedMoviesWarningResolverRef.current(accepted);
      generatedMoviesWarningResolverRef.current = null;
    }
    setShowGeneratedMoviesWarningModal(false);
  }, []);

  const requestGeneratedMoviesWarningConfirmation = useCallback((): Promise<boolean> => {
    if (generatedMoviesWarningResolverRef.current) {
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      generatedMoviesWarningResolverRef.current = resolve;
      setShowGeneratedMoviesWarningModal(true);
    });
  }, []);

  useEffect(
    () => () => {
      if (generatedMoviesWarningResolverRef.current) {
        generatedMoviesWarningResolverRef.current(false);
        generatedMoviesWarningResolverRef.current = null;
      }
    },
    [],
  );

  const ensureGeneratedMoviesWarningBeforeMutation = useCallback(
    async (targetFeedId?: string): Promise<boolean> => {
      const normalizedFeedId = String(targetFeedId || feedId || '');
      if (!normalizedFeedId) return true;

      if (acceptedGeneratedMoviesWarningRef.current[normalizedFeedId]) {
        return true;
      }

      try {
        const [standalone, standalonePadding] = await Promise.all([
          estimateComposerDownload(normalizedFeedId, { mode: 'angles' as any }),
          estimateComposerDownload(normalizedFeedId, { mode: 'anglesPaddingJson' as any }),
        ]);

        const getReusedOutputs = (response: any) => {
          const estimate = response?.data ?? response;
          const reusedOutputs = Number(estimate?.reusedOutputs ?? 0);
          return Number.isFinite(reusedOutputs) ? reusedOutputs : 0;
        };

        const hasGeneratedMovies = getReusedOutputs(standalone) > 0 || getReusedOutputs(standalonePadding) > 0;
        if (!hasGeneratedMovies) return true;
      } catch (error) {
        console.warn('Failed to check generated standalone movies', error);
        return true;
      }

      const accepted = await requestGeneratedMoviesWarningConfirmation();
      if (accepted) {
        acceptedGeneratedMoviesWarningRef.current[normalizedFeedId] = true;
      }
      return accepted;
    },
    [feedId, requestGeneratedMoviesWarningConfirmation],
  );

  const videoLengthInSeconds: number = useMemo(() => {
    return composerPlayControl.composerVideoLength / 1000;
  }, [composerPlayControl.composerVideoLength]);

  const clipAvailabilityClipIds = useMemo(() => {
    const clipIds = new Set<number>();
    composerRows.forEach((topic) => {
      (topic?.clips || []).forEach((clip: any) => {
        const clipId = Number(clip?.id);
        if (Number.isFinite(clipId)) {
          clipIds.add(clipId);
        }
      });
    });
    return Array.from(clipIds).sort((a, b) => a - b);
  }, [composerRows]);
  const clipAvailabilitySignature = useMemo(() => {
    const signature: string[] = [];
    composerRows.forEach((topic) => {
      (topic?.clips || []).forEach((clip: any) => {
        signature.push(`${clip?.id}:${clip?.time_start}:${clip?.time_end}`);
      });
    });
    return signature.join('|');
  }, [composerRows]);
  const clipAvailabilityRequestKey = useMemo(
    () => `${String(feedId || '')}:${clipAvailabilityClipIds.join(',')}:${clipAvailabilitySignature}`,
    [feedId, clipAvailabilityClipIds, clipAvailabilitySignature],
  );
  const [generatedClipIds, setGeneratedClipIds] = useState<string[]>([]);
  const generatedClipIdSet = useMemo(() => new Set(generatedClipIds), [generatedClipIds]);

  useEffect(() => {
    const normalizedFeedId = String(feedId || '');
    if (!normalizedFeedId || !clipAvailabilityClipIds.length) {
      setGeneratedClipIds([]);
      return;
    }

    let cancelled = false;
    const fetchGeneratedClipIds = async () => {
      try {
        const res = await getGeneratedComposerClipIds(normalizedFeedId, { clipIds: clipAvailabilityClipIds });
        const generatedIds = res?.generatedClipIds ?? res?.payload?.generatedClipIds;
        const ids = Array.isArray(generatedIds) ? generatedIds.map((id: any) => String(id)) : [];
        if (!cancelled) {
          setGeneratedClipIds(ids);
        }
      } catch (error) {
        if (!cancelled) {
          setGeneratedClipIds([]);
        }
      }
    };

    fetchGeneratedClipIds();

    return () => {
      cancelled = true;
    };
  }, [clipAvailabilityRequestKey]);

  const [clipDrawer, setClipDrawer] = useState<TClipDrawer>(null);
  const [clipEditorZoom, setClipEditorZoom] = useState(1);
  const [clipEditorFitRequest, setClipEditorFitRequest] = useState<{ center: number } | null>(null);
  const hasOriginalClipBounds = Boolean(
    clipDrawer?.original_time_start != null &&
      clipDrawer?.original_time_end != null &&
      Number.isFinite(Number(clipDrawer.original_time_start)) &&
      Number.isFinite(Number(clipDrawer.original_time_end)) &&
      clipDrawer.original_time_end > clipDrawer.original_time_start,
  );
  const pauseComposerPlayback = useCallback(() => {
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playing: false,
      }),
    );
  }, [composerPlayControl.composerControls, dispatch]);

  const switchColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const onLocateDrawer = () => {
    if (clipDrawer?.id) {
      document.getElementById(clipDrawer?.id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  };

  const handleStartChange = (e: any) => {
    const newStart = Number(e.target.value.replace(/\D/g, ''));

    if (hasFixedClipBounds && clipDrawer) {
      const originalStart = clipDrawer.original_time_start ?? clipDrawer.time_start;
      const boundedStart = Math.min(Math.max(newStart, originalStart), clipDrawer.time_end - 1);
      if (boundedStart !== newStart) {
        notification.showMessage('Clip cannot be expanded beyond its original range.');
      }
      setClipDrawer({ ...clipDrawer, time_start: boundedStart });
      dispatch(updateCustomTime(boundedStart));
      return;
    }

    if (newStart > videoLength) {
      notification.showMessage('Clip duration is out of video length.');
      setClipDrawer({ ...clipDrawer!, time_start: Math.floor(videoLength) - 1, time_end: Math.floor(videoLength) });

      dispatch(updateCustomTime(newStart));
      return;
    }

    setClipDrawer({ ...clipDrawer!, time_start: newStart });
    dispatch(updateCustomTime(newStart));
  };

  const handleEndChange = (e: any) => {
    const newEnd = Number(e.target.value.replace(/\D/g, ''));

    if (hasFixedClipBounds && clipDrawer) {
      const originalEnd = clipDrawer.original_time_end ?? clipDrawer.time_end;
      const boundedEnd = Math.max(Math.min(newEnd, originalEnd), clipDrawer.time_start + 1);
      if (boundedEnd !== newEnd) {
        notification.showMessage('Clip cannot be expanded beyond its original range.');
      }
      setClipDrawer({ ...clipDrawer, time_end: boundedEnd });
      dispatch(updateCustomTime(boundedEnd));
      return;
    }

    if (newEnd > videoLength) {
      notification.showMessage('Clip duration is out of video length.');
    }

    const finalEnd = newEnd > videoLength ? videoLength : newEnd;
    setClipDrawer({ ...clipDrawer!, time_end: Math.floor(finalEnd) });
    dispatch(updateCustomTime(Math.floor(finalEnd)));
  };

  const dismissClipDrawer = useCallback(() => {
    setShowColorPicker(false);
    clipEditorDragViewportRef.current = null;
    setDragging(null);
    setClipDrawer(null);
  }, []);

  const [initialClipDrawer, setInitialClipDrawer] = useState<{ time_start: number; time_end: number; color?: string; id: number }>({
    time_start: 0,
    time_end: 0,
    color: '#7C7C72',
    id: 0,
  });

  const cancelClipEditing = useCallback(() => {
    dispatch(updateCustomTime(initialClipDrawer.time_start));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playing: false,
        playedSeconds: initialClipDrawer.time_start,
      }),
    );
    dispatch(triggerRefresh());
    dismissClipDrawer();
  }, [composerPlayControl.composerControls, dismissClipDrawer, dispatch, initialClipDrawer.time_start]);

  const onCancelDrawerClick = () => {
    setClipDrawer({
      ...clipDrawer!,
      time_start: initialClipDrawer.time_start,
      time_end: initialClipDrawer.time_end,
      color: initialClipDrawer.color,
    });
  };

  const initialXRef = useRef<number>(0);
  const initialStartRef = useRef<number>(0);
  const initialEndRef = useRef<number>(0);
  const initialPlayheadRef = useRef<number>(0);
  const resizePixelsPerSecondRef = useRef<number>(markMargin);
  const clipEditorTrackRef = useRef<HTMLDivElement>(null);
  const clipEditorViewportRef = useRef<HTMLDivElement>(null);
  const clipEditorDragViewportRef = useRef<number | null>(null);
  const clipEditorSeekFrameRef = useRef<number | null>(null);
  const previousClipEditorPlayheadRef = useRef<{ clipId?: string; time: number } | null>(null);
  const [clipEditorScrollLeft, setClipEditorScrollLeft] = useState(0);
  // Keep the time-to-pixel scale stable for the entire edit session, including mouseup.
  const [clipEditorSourceDuration, setClipEditorSourceDuration] = useState(1);
  const clipEditorMaxZoom = Math.max(BASE_MAX_CLIP_EDITOR_ZOOM, clipEditorSourceDuration / 5);
  const clipEditorPlayhead = Math.max(
    0,
    Math.min(
      clipEditorSourceDuration,
      composerPlayControl.composerControls.playedSeconds > 0
        ? composerPlayControl.composerControls.playedSeconds
        : customTime,
    ),
  );
  const clipEditorStart = clipDrawer ? Math.max(0, Math.min(100, (clipDrawer.time_start / clipEditorSourceDuration) * 100)) : 0;
  const clipEditorWidth = clipDrawer
    ? Math.max(
        0,
        Math.min(100 - clipEditorStart, ((clipDrawer.time_end - clipDrawer.time_start) / clipEditorSourceDuration) * 100),
      )
    : 0;
  const clipEditorTimestamps = useMemo(() => {
    const visibleDuration = clipEditorSourceDuration / clipEditorZoom;
    const viewportWidth = clipEditorViewportRef.current?.clientWidth || 1;
    const visibleStart = Math.max(0, (clipEditorScrollLeft / viewportWidth) * visibleDuration);
    const interval =
      CLIP_EDITOR_TICK_INTERVALS.find((candidate) => candidate >= visibleDuration / 6) ??
      CLIP_EDITOR_TICK_INTERVALS[CLIP_EDITOR_TICK_INTERVALS.length - 1];
    const firstTick = Math.floor(visibleStart / interval) * interval;
    const lastTick = Math.min(clipEditorSourceDuration, visibleStart + visibleDuration + interval);
    const timestamps = [];

    for (let time = firstTick; time <= lastTick; time += interval) {
      timestamps.push({ left: (time / clipEditorSourceDuration) * 100, time });
    }
    return timestamps;
  }, [clipEditorScrollLeft, clipEditorSourceDuration, clipEditorZoom]);
  const updateClipEditorPlayhead = useCallback(
    (time: number) => {
      const target = Math.max(0, Math.min(clipEditorSourceDuration, time));
      dispatch(updateCustomTime(target));
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: false,
          playedSeconds: target,
        }),
      );
    },
    [clipEditorSourceDuration, composerPlayControl.composerControls, dispatch],
  );

  const scheduleClipEditorPlayheadUpdate = useCallback(
    (time: number, keepViewport = false) => {
      if (clipEditorSeekFrameRef.current != null) cancelAnimationFrame(clipEditorSeekFrameRef.current);
      clipEditorSeekFrameRef.current = requestAnimationFrame(() => {
        // A body drag must not trigger follow-playhead scrolling, even after mouseup.
        if (keepViewport) previousClipEditorPlayheadRef.current = { clipId: clipDrawer?.id, time };
        updateClipEditorPlayhead(time);
      });
    },
    [clipDrawer?.id, updateClipEditorPlayhead],
  );

  useEffect(
    () => () => {
      if (clipEditorSeekFrameRef.current != null) cancelAnimationFrame(clipEditorSeekFrameRef.current);
    },
    [],
  );

  const handleClipResizeStart = (e: React.MouseEvent, side: 'left' | 'right' | 'center') => {
    e.preventDefault();
    e.stopPropagation();
    if (!clipDrawer) return;

    initialXRef.current = e.clientX;
    initialStartRef.current = clipDrawer.time_start;
    initialEndRef.current = clipDrawer.time_end;
    initialPlayheadRef.current = clipEditorPlayhead;
    clipEditorDragViewportRef.current = side === 'center'
      ? clipEditorViewportRef.current?.scrollLeft ?? 0
      : null;
    const trackWidth = clipEditorTrackRef.current?.getBoundingClientRect().width || 0;
    resizePixelsPerSecondRef.current = trackWidth > 0 ? trackWidth / clipEditorSourceDuration : markMargin;
    setDragging(side);
  };

  const handleClipPlayheadDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    initialXRef.current = e.clientX;
    initialPlayheadRef.current = clipEditorPlayhead;
    const trackWidth = clipEditorTrackRef.current?.getBoundingClientRect().width || 0;
    resizePixelsPerSecondRef.current = trackWidth > 0 ? trackWidth / clipEditorSourceDuration : markMargin;
    setDragging('playhead');
  };

  const handleClipEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!clipDrawer || !clipEditorTrackRef.current) return;

    const trackRect = clipEditorTrackRef.current.getBoundingClientRect();
    const clickedTime = Math.max(
      0,
      Math.min(clipEditorSourceDuration, ((e.clientX - trackRect.left) / trackRect.width) * clipEditorSourceDuration),
    );
    updateClipEditorPlayhead(clickedTime);
  };

  const fitClipEditorToClip = () => {
    if (!clipDrawer) return;
    const visibleDuration = Math.min(clipEditorSourceDuration, Math.max(5, (clipDrawer.time_end - clipDrawer.time_start) * 1.2));
    setClipEditorZoom(clipEditorSourceDuration / visibleDuration);
    // A new request also recenters after manual scrolling when the zoom hasn't changed.
    setClipEditorFitRequest({ center: (clipDrawer.time_start + clipDrawer.time_end) / 2 });
  };

  useEffect(() => {
    if (!clipDrawer) {
      setClipEditorZoom(1);
      return;
    }
    if (!clipEditorViewportRef.current || clipEditorDragViewportRef.current != null) return;

    const clipCenter =
      ((clipEditorFitRequest?.center ?? (initialClipDrawer.time_start + initialClipDrawer.time_end) / 2) / clipEditorSourceDuration) *
      clipEditorViewportRef.current.clientWidth *
      clipEditorZoom;
    const maxScrollLeft = clipEditorViewportRef.current.clientWidth * (clipEditorZoom - 1);
    const scrollLeft = Math.min(maxScrollLeft, Math.max(0, clipCenter - clipEditorViewportRef.current.clientWidth / 2));
    clipEditorViewportRef.current.scrollLeft = scrollLeft;
    setClipEditorScrollLeft(scrollLeft);
  }, [clipDrawer?.id, clipEditorSourceDuration, clipEditorZoom, clipEditorFitRequest, initialClipDrawer.time_end, initialClipDrawer.time_start]);

  useEffect(() => {
    if (!clipDrawer || !clipEditorViewportRef.current) {
      previousClipEditorPlayheadRef.current = null;
      return;
    }

    const previous = previousClipEditorPlayheadRef.current;
    previousClipEditorPlayheadRef.current = { clipId: clipDrawer.id, time: clipEditorPlayhead };
    if (dragging || !previous || previous.clipId !== clipDrawer.id || Math.abs(previous.time - clipEditorPlayhead) < 0.0001) return;

    const viewport = clipEditorViewportRef.current;
    const playheadLeft = (clipEditorPlayhead / clipEditorSourceDuration) * viewport.clientWidth * clipEditorZoom;
    const edgePadding = 16;
    if (playheadLeft >= viewport.scrollLeft + edgePadding && playheadLeft <= viewport.scrollLeft + viewport.clientWidth - edgePadding) {
      return;
    }

    const maxScrollLeft = viewport.clientWidth * (clipEditorZoom - 1);
    const scrollLeft = Math.min(maxScrollLeft, Math.max(0, playheadLeft - viewport.clientWidth / 2));
    viewport.scrollLeft = scrollLeft;
    setClipEditorScrollLeft(scrollLeft);
  }, [clipDrawer?.id, clipEditorPlayhead, clipEditorSourceDuration, clipEditorZoom, dragging]);

  const sessionUpdate = async () => {
    clipDrawer && setClipDrawer(null);
    setShowColorPicker(false);
  };

  const onClipCreate = async (clipData?: any) => {
    if ((clipDrawer || clipData) && feedId) {
      const editedClipTimes = clipDrawer?.isEdit
        ? { time_start: clipDrawer.time_start, time_end: clipDrawer.time_end }
        : null;
      try {
        let res: any;
        if (clipDrawer?.isEdit) {
          const canProceed = await ensureGeneratedMoviesWarningBeforeMutation();
          if (!canProceed) {
            return;
          }
        }
        setLoading(true);
        // if (clipData) {
        //   res = await createClipForTopic(
        //     {
        //       topic_id: +clipData.topicId,
        //       time_start: clipData.time_start,
        //       time_end: clipData.time_end,
        //       color: clipData.color ? colorParser(clipData.color) : '',
        //     },
        //     +feedId,
        //   );
        // } else
        if (clipDrawer?.isEdit) {
          const { time_start: oldStart, time_end: oldEnd, color: oldColor, id } = initialClipDrawer;

          // 2️⃣ Push undo action
          pushUndo({
            exec: async () => {
              await editClip({ time_start: oldStart, time_end: oldEnd, color: oldColor! }, +id!);
              dispatch(triggerRefresh());
            },
          });

          // 3️⃣ Perform the real edit
          res = await editClip(
            { time_start: clipDrawer.time_start, time_end: clipDrawer.time_end, color: clipDrawer.color! },
            +clipDrawer.id!,
          );
        }
        // else if (clipDrawer) {
        //   res = await createClipForTopic(
        //     {
        //       topic_id: +clipDrawer.topicId,
        //       time_start: clipDrawer.time_start,
        //       time_end: clipDrawer.time_end,
        //       color: clipDrawer.color ? colorParser(clipDrawer.color) : '',
        //     },
        //     +feedId,
        //   );
        // }
        if (res) {
          await sessionUpdate();
        }
        // if (res?.data?.id) {
        //   if (clipDrawer && clipDrawer?.isEdit) {
        //     const selectedTopic = videoRows.find((row) => row.id === clipDrawer.topicId);
        //     const selectedClip = selectedTopic!.clips.find((clip) => clip.id === clipDrawer.id);

        //     const { id, color, time_start, time_end, topicId } = selectedClip!;
        //     setSession([
        //       ...session,
        //       {
        //         clip: {
        //           id: id!,
        //           color: color || '1.0,1.0,1.0',
        //           time_start: time_start.toString(),
        //           time_end: time_end.toString(),
        //           status: 'edited',
        //           topicId: topicId!,
        //         },
        //       },
        //     ]);
        //   } else if (!clipData) {
        //     const {
        //       color,
        //       id,
        //       time_start,
        //       time_end,
        //       topic: { id: topicId },
        //     } = res.data;
        //     setSession([...session, { clip: { id, color, time_start, time_end, status: 'created', topicId } }]);
        //   }
        // }
        await getComposer(feedId, true);
        const clip = currentSelectedComposerClips.find((c) => c.id == clipDrawer?.id);
        if (clip) {
          selectClip(editedClipTimes ? { ...clip, ...editedClipTimes } : clip, clip!.clipIndex!, clip!.rowIndex!);
          dispatch(triggerRefresh());
        }
        if (editedClipTimes) {
          dispatch(updateCustomTime(editedClipTimes.time_start));
          dispatch(
            updateComposerPlayControls({
              ...composerPlayControl.composerControls,
              playing: false,
              playedSeconds: editedClipTimes.time_start,
            }),
          );
          if (!clip) dispatch(triggerRefresh());
        }
        return res.data;
      } catch (e: any) {
        console.error(e);
        if (e?.response?.data?.message) {
          notification.showMessage(e?.response?.data?.message);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !clipDrawer || resizePixelsPerSecondRef.current <= 0) return;
    const deltaPixels = e.clientX - initialXRef.current;
    const deltaSeconds = deltaPixels / resizePixelsPerSecondRef.current;
    if (dragging === 'playhead') {
      scheduleClipEditorPlayheadUpdate(initialPlayheadRef.current + deltaSeconds);
    } else if (dragging === 'left') {
      let newStart = initialStartRef.current + deltaSeconds;
      newStart = Math.min(newStart, clipDrawer.time_end - 1);
      newStart = Math.max(
        newStart,
        hasFixedClipBounds ? Number(clipDrawer.original_time_start ?? initialStartRef.current) : 0,
      );
      setClipDrawer({ ...clipDrawer, time_start: newStart });
      scheduleClipEditorPlayheadUpdate(newStart);
    } else if (dragging === 'right') {
      let newEnd = initialEndRef.current + deltaSeconds;
      newEnd = Math.max(newEnd, clipDrawer.time_start + 1);
      newEnd = Math.min(
        newEnd,
        hasFixedClipBounds
          ? Number(clipDrawer.original_time_end ?? initialEndRef.current)
          : clipEditorSourceDuration,
      );
      setClipDrawer({ ...clipDrawer, time_end: newEnd });
      scheduleClipEditorPlayheadUpdate(newEnd);
    } else if (dragging === 'center') {
      const clipDuration = initialEndRef.current - initialStartRef.current;
      const minimumStart = hasFixedClipBounds
        ? Number(clipDrawer.original_time_start ?? initialStartRef.current)
        : 0;
      const maximumEnd = hasFixedClipBounds
        ? Number(clipDrawer.original_time_end ?? initialEndRef.current)
        : clipEditorSourceDuration;
      const newStart = Math.min(Math.max(initialStartRef.current + deltaSeconds, minimumStart), maximumEnd - clipDuration);
      setClipDrawer({ ...clipDrawer, time_start: newStart, time_end: newStart + clipDuration });
      if (initialPlayheadRef.current >= initialStartRef.current && initialPlayheadRef.current <= initialEndRef.current) {
        scheduleClipEditorPlayheadUpdate(newStart + initialPlayheadRef.current - initialStartRef.current, true);
      }
    }
  };

  const handleEditClip = ({ id, time_end, time_start, original_time_start, original_time_end, color, topicId, videos }: CLIP) => {
    const start = Number(time_start);
    const end = Number(time_end);
    const originalStart = Number(original_time_start);
    const originalEnd = Number(original_time_end);
    const hasValidOriginalBounds =
      original_time_start != null &&
      original_time_end != null &&
      Number.isFinite(originalStart) &&
      Number.isFinite(originalEnd) &&
      originalEnd > originalStart;
    const clipDuration = Math.max(end - start, 1);
    const angleIndex = Number(findSearchValue('clip')) || 0;
    const sourceVideo = videos?.length ? videos[angleIndex] ?? videos[0] : videoData;
    const metadataDuration = Number(sourceVideo?.duration);
    const playerDuration = !sourceVideo || sourceVideo.id === videoData?.id ? Number(composerSourceDuration) : 0;
    const sourceDuration = Math.max(
      Number.isFinite(playerDuration) && playerDuration > 0
        ? playerDuration
        : Number.isFinite(metadataDuration) && metadataDuration > 0 ? metadataDuration : 0,
      end,
      1,
    );
    const visibleDuration = Math.min(sourceDuration, Math.max(15, clipDuration * 2.5));
    pauseComposerPlayback();
    setClipEditorSourceDuration(sourceDuration);
    setClipEditorZoom(Math.max(1, sourceDuration / visibleDuration));
    setClipEditorFitRequest(null);
    setInitialClipDrawer({
      time_start: start,
      time_end: end,
      color: parseColorToHex(color ?? '#6500aa'),
      id: +id,
    });

    setClipDrawer({
      time_start: start,
      time_end: end,
      id,
      color: parseColorToHex(color ?? '#6500aa'),
      topicId: topicId!,
      isEdit: true,
      original_time_start: hasValidOriginalBounds ? originalStart : start,
      original_time_end: hasValidOriginalBounds ? originalEnd : end,
    });
  };

  const setClipBoundaryToPlayhead = useCallback(
    (boundary: 'in' | 'out') => {
      if (!clipDrawer || !canTrimClipToPlayhead) return;

      const playhead =
        composerPlayControl.composerControls.playedSeconds > 0
          ? composerPlayControl.composerControls.playedSeconds
          : customTime;

      if (
        hasFixedClipBounds &&
        (playhead < (clipDrawer.original_time_start ?? clipDrawer.time_start) ||
          playhead > (clipDrawer.original_time_end ?? clipDrawer.time_end))
      ) {
        notification.showMessage('Playhead is outside the original clip range.');
        return;
      }
      if (!hasFixedClipBounds && (playhead < 0 || playhead > videoLength)) {
        notification.showMessage('Playhead is outside the video range.');
        return;
      }

      const newStart = boundary === 'in' ? playhead : clipDrawer.time_start;
      const newEnd = boundary === 'out' ? playhead : clipDrawer.time_end;
      if (newEnd - newStart < 1) {
        notification.showMessage('Clip must be at least one second long.');
        return;
      }

      setClipDrawer({ ...clipDrawer, time_start: newStart, time_end: newEnd });
    },
    [
      canTrimClipToPlayhead,
      clipDrawer,
      composerPlayControl.composerControls.playedSeconds,
      customTime,
      hasFixedClipBounds,
      notification,
      videoLength,
    ],
  );

  const restoreOriginalClipBoundary = useCallback(
    (boundary: 'in' | 'out') => {
      if (!clipDrawer || !hasOriginalClipBounds) return;

      const originalStart = Number(clipDrawer.original_time_start);
      const originalEnd = Number(clipDrawer.original_time_end);

      setClipDrawer({
        ...clipDrawer,
        time_start: boundary === 'in' ? originalStart : clipDrawer.time_start,
        time_end: boundary === 'out' ? originalEnd : clipDrawer.time_end,
      });
    },
    [clipDrawer, hasOriginalClipBounds],
  );

  useHotkeys(
    'I',
    (event) => {
      event.preventDefault();
      setClipBoundaryToPlayhead('in');
    },
    { enabled: Boolean(clipDrawer && canTrimClipToPlayhead), preventDefault: true },
    [setClipBoundaryToPlayhead],
  );

  useHotkeys(
    'O',
    (event) => {
      event.preventDefault();
      setClipBoundaryToPlayhead('out');
    },
    { enabled: Boolean(clipDrawer && canTrimClipToPlayhead), preventDefault: true },
    [setClipBoundaryToPlayhead],
  );

  useEffect(() => {
    if (feedId && previousFeed !== feedId) {
      previousFeed = feedId;
      autoZoomed = false;
    }

    return () => {
      previousFeed = '';
      autoZoomed = false;
    };
  }, [feedId]);

  function selectFirstClipIfAny(rows: TComposerClip[]) {
    const firstRowIndex = rows.findIndex((r) => r.clips?.length);
    if (firstRowIndex === -1) return;

    const firstClip = rows[firstRowIndex].clips[0];
    const selection = [
      {
        ...firstClip,
        clipIndex: 0,
        rowIndex: firstRowIndex,
        topicId: rows[firstRowIndex].id,
        topicName: rows[firstRowIndex].name,
      },
    ];

    dispatch(updateCurrentSelectedComposerClips(selection));
    dispatch(updateCustomTime(firstClip.time_start));
    dispatch(setComposerMarkerPosition({ rowIndex: firstRowIndex, clipIndex: 0 }));
  }

  const runCheck = async (type: 'folder' | 'videofeed', id: string) => {
    const allContent = findSearchValue('allContent');
    const parentFolders = findSearchValue('parentFolders');
    if (allContent === 'true' || parentFolders === 'true') return;
    try {
      const res = await checkContentAvailability(type, id);
      if (res.data?.deleted) {
        dispatch(
          updateShowWarningModal({ show: true, warningText: 'The content you are trying to access has been deleted', redirect: true }),
        );
      } else if (res.data?.noAccess) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: 'You do not have permission to access the content you are trying to view.',
            redirect: true,
          }),
        );
      } else if (res?.data?.differentOrg) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: `Your current organisation is different from the content's organisation.${
              res?.data?.organizationName ? ` The content is from ${res.data.organizationName}.` : ''
            }`,
            redirect: true,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const lastFeedIdRef = useRef<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(false);
  const getComposer = async (id: string, ignoreCache = false) => {
    setLoading(true);
    if (!initialLoad) {
      setInitialLoad(true);
    }
    try {
      runCheck('videofeed', id);
      const { data } = await getComposerDataByVideoFeedId(id, ignoreCache ? { cache: false } : undefined);

      // normalize – in case in future it becomes just VIDEOFEED
      const composer = data.payload ?? data;

      dispatch(updateComposerReady(composer));

      if (lastFeedIdRef.current !== id) {
        selectFirstClipIfAny(composer.topics);
        lastFeedIdRef.current = id;
      }

      dispatch(updateComposerRows(composer.topics));
      dispatch(saveCurrentComposerVideos(composer.videos));
      calculateTotalClipsLength(composer.topics, composer);

      setComposerData(composer);
      return composer.topics;
    } catch (err: any) {
      console.warn(err);
      return [];
    } finally {
      setInitialLoad(false);
      setLoading(false);
    }
  };

  useHotkeys('Space', () => {
    if (!initialLoad) {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: !composerPlayControl.composerControls.playing,
        }),
      );
      dispatch(toFollowPlayhead(!composerPlayControl.composerControls.playing));
    }
  });

  const [longestRowSeconds, setLongestRowSeconds] = useState(0);
  const [longestRowClipsNumber, setLongestRowClipsNumber] = useState(0);
  const calculateTotalClipsLength = (composerTopics: typeof composerRows, composerJson: VIDEOFEED) => {
    let totalClipsLength = 0;
    let anglesCount = 0;

    let maxRow = 0;
    let totalClipsNumber = 0;
    composerTopics.forEach((topic) => {
      let max = 0;
      let clipsNumber = 0;
      topic.clips.forEach((clip) => {
        max += clip.time_end - clip.time_start;
        clipsNumber++;
        if (clip?.videos && clip?.videos?.length > anglesCount) {
          anglesCount = clip.videos.length;
        }
        totalClipsLength += clip.time_end - clip.time_start;
      });
      if (max > maxRow) {
        maxRow = max;
        totalClipsNumber = clipsNumber;
      }
    });

    if (anglesCount < composerJson.videos.length) {
      anglesCount = composerJson.videos.length;
    }
    dispatch(updateAnglesCount(anglesCount));
    dispatch(updateComposerVideoLength(totalClipsLength));
    setLongestRowClipsNumber(totalClipsNumber);
    setLongestRowSeconds(maxRow);
  };

  const calculateWidthOfClips = (composersList: TClip[], margin: number) => {
    const composersInital = JSON.parse(JSON.stringify(composersList));
    composersInital.forEach((topic: TComposerFile, rowIndex: number) => {
      topic.clips.forEach((clip: TClip, clipIndex: number) => {
        clip.width = (clip.time_end - clip.time_start) * margin;
        clip.clipIndex = clipIndex;
        clip.rowIndex = rowIndex;
      });
    });
    dispatch(updateComposerRows(composersInital));
  };

  useEffect(() => {
    if (composerPlayControl.isDragging) {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: false,
        }),
      );
    } else if (!composerPlayControl.isDragging && composerRows.length > 0) {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: true,
        }),
      );
    }
  }, [composerPlayControl.isDragging]);

  const getComposersAndCalculateWidth = async (margin: number) => {
    const topics = await getComposer(feedId as string);
    if (topics) {
      calculateWidthOfClips(topics, margin);
    }
  };

  useEffect(() => {
    if (composerRows.length > 0) {
      calculateWidthOfClips(composerRows as unknown as TClip[], markMargin);
    }
  }, [markMargin, feedId, maxMarkMargin]);

  useEffect(() => {
    if (!feedId) return;

    // hard reset BEFORE fetching next videofeed
    dispatch(updateComposerRows([]));
    dispatch(updateCurrentSelectedComposerClips([]));
    dispatch(setComposerMarkerPosition({ rowIndex: 0, clipIndex: 0 }));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: 0,
        playing: false,
      }),
    );

    getComposersAndCalculateWidth(markMargin);
    setStartIndexForShiftSelect(null);

    return () => {
      dispatch(clearComposerData());
    };
  }, [feedId, triggerForRefetching]);

  useEffect(() => {
    if (composerData) {
      dispatch(updateCurrentComposerData(composerData));
      if (isContentAdminOA || composerData?.created_by?.id === currentUser?.id) {
        setUserAccess(ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE);
      } else if (composerData && composerData.highest_user_access) {
        setUserAccess(composerData?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM);
      }
    }
  }, [composerData, currentUser, isContentAdminOA]);

  useEffect(() => {
    if (findSearchValue('sharedComposer')) {
      dispatch(getAllSharedComposerThunk());
    } else {
      dispatch(getAllComposersThunk());
    }

    return () => {
      dispatch(clearComposerData());
    };
  }, []);

  const [minAutoMargin, setMinAutoMargin] = useState<number>(0.2);
  const [maxAutoMargin, setMaxAutoMargin] = useState<number>(4);
  const handleZoomOut = useCallback(() => {
    dispatch(updateMarkMargin(markMargin - 0.3));
  }, [markMargin, minAutoMargin]);

  const handleZoomIn = useCallback(() => {
    dispatch(updateMarkMargin(markMargin + 0.3));
  }, [markMargin, maxAutoMargin]);

  const zoomInHandlers = useAutoRepeat(handleZoomIn, {
    delay: 400,
    interval: 80,
    disabled: markMargin >= maxAutoMargin,
  });
  const zoomOutHandlers = useAutoRepeat(handleZoomOut, {
    delay: 400,
    interval: 80,
    disabled: markMargin <= minAutoMargin,
  });

  const zoomToFit = (e?: any) => {
    e?.preventDefault();
    dispatch(updateMarkMargin(maxAutoMargin));
  };

  const zoomToMax = (e?: any) => {
    e?.preventDefault();
    dispatch(updateMarkMargin(minAutoMargin));
  };

  const onPlayPause = () => {
    dispatch(toFollowPlayhead(!composerPlayControl.composerControls.playing));
    if (composerPlayControl.composerControls.playedSeconds < customTime) {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playedSeconds: 0,
          playing: !composerPlayControl.composerControls.playing,
        }),
      );
    } else {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: !composerPlayControl.composerControls.playing,
        }),
      );
    }
  };

  const handleEditSubmit = async (topicId: string, name: string, rowIndex: number) => {
    if (!feedId) return;
    try {
      setLoading(true);

      const oldName = composerRows[rowIndex].name;

      // 2) Build an undo action for this rename
      const undoRename: UndoAction = {
        exec: async (overrideTopicId) => {
          const targetId = overrideTopicId ?? topicId;
          await updateTopicName(`${targetId}`, { name: oldName });
          dispatch(triggerRefresh());
        },
      };

      // 3a) Push it onto the global undo stack
      pushUndo(undoRename);

      // 3b) Also register under this topic’s bucket
      renameUndoMapRef.current[topicId] = renameUndoMapRef.current[topicId] || [];
      renameUndoMapRef.current[topicId].push(undoRename);

      await updateTopicName(topicId, { name });
      enqueueSnackbar(NOTIFICATION.SUCCESSFUL_NAME_CHANGE, {
        variant: 'success',
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
      });
      return await getComposer(feedId);
    } catch (error) {
      enqueueSnackbar(NOTIFICATION.NOT_EDITED, {
        variant: 'error',
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
      });
    } finally {
      setLoading(false);
    }
  };

  const [createRowModal, setCreateRowModal] = useState(false);
  const switchCreateRowModal = () => {
    if (!createRowModal) {
      pauseComposerPlayback();
    }
    setCreateRowModal(!createRowModal);
  };

  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);

  useEffect(() => {
    setUndoStack([]);
  }, [emptyComposerStackActionTrigger]);

  const clipUndoMapRef = useRef<Record<string, UndoAction[]>>({});
  const renameUndoMapRef = useRef<Record<string, UndoAction[]>>({});
  const undoStep = async () => {
    if (quickLoader || !undoStack.length) return;

    setQuickLoader(true);
    try {
      const action = undoStack[undoStack.length - 1];
      setUndoStack((s) => s.slice(0, -1));
      await action.exec();

      dispatch(updateMarkMargin(dynamicZoom ? markMargin + 0.00001 : markMargin - 0.00001));
      await getComposersAndCalculateWidth(markMargin);
      // dispatch(triggerRefresh());
      // dispatch(setFetchComments());
    } catch (err) {
      console.warn(err);
    } finally {
      setQuickLoader(false);
    }
  };

  useHotkeys(['Control+z', 'Meta+z'], () => {
    if (!quickLoader && undoStack.length > 0) undoStep();
  });

  const [quickLoader, setQuickLoader] = useState(false);
  const reorderRows = async (rows: TComposerClip[], directFetch?: boolean, sessionRowsArrayFromArguments?: TSessionRows[]) => {
    const parsedRows = {
      topics: rows?.map((topic) => {
        const clipsMap = new Map();

        // Filter out duplicate clips within the same topic
        const uniqueClips = topic?.clips?.filter((clip) => {
          if (!clipsMap.has(clip?.id)) {
            clipsMap.set(clip?.id, true);
            return true;
          }
          return false;
        });

        return {
          id: topic?.id,
          clips: uniqueClips?.map((clip, idx) => ({
            id: clip?.id,
            clipIndex: idx,
          })),
        };
      }),
      rows: rows,
    };

    setQuickLoader(true);
    try {
      await patchReoderComposer(parsedRows, feedId as string);
      dispatch(triggerRefresh());
      dispatch(setFetchComments());
    } catch (err) {
      console.warn(err);
    } finally {
      setQuickLoader(false);
    }
  };

  const pushUndo = (action: UndoAction) => setUndoStack((s: UndoAction[]) => [...s, action]);

  const createdNewRowCallBack = async ({ id, name, topicIndex }: TOPIC) => {
    const feedId = findSearchValue('feedId') as string;
    const pageType = findSearchValue('pageType') as string;
    setLoading(true);

    try {
      pushUndo({
        exec: async () => {
          switch (pageType) {
            case 'MY_COMPOSER':
              await deleteTopicInReflexComposer(id, feedId);
              break;
            case 'COMPOSER':
              await deleteTopicInComposer(id, feedId);
              break;
            default:
              break;
          }
          dispatch(triggerRefetch());
          dispatch(setFetchComments());
        },
      });

      dispatch(triggerRefetch());
      switchCreateRowModal();
    } catch (err: any) {
      console.error(err);
      notification.showMessage(err?.response?.data?.message || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  function lineCoordToFlatIndex(
    clips: TClip[],
    markMargin: number,
    lineIdx: number,
    jInLine: number,
    gap = COMPOSER_CLIP_RIGHT_MARGIN,
  ): number {
    let accW = 0; // width accumulated so far
    let flat = 0; // index we’ll return

    for (let i = 0; i < clips.length; i++) {
      const w = (clips[i].time_end - clips[i].time_start) * markMargin + gap;

      // did we hit the wanted line?
      if (accW + w > rowWidth) {
        lineIdx--;
        accW = 0;
      }
      if (lineIdx === 0 && jInLine === 0) return flat;

      // advance
      if (lineIdx === 0) jInLine--;
      accW += w;
      flat++;
    }
    return flat; // fallback (shouldn’t really happen)
  }

  // NEW — helpers to snapshot and restore playhead
  function getActiveClipSnapshot(rows: TComposerClip[], ctrl: typeof composerPlayControl) {
    const { currentRowIndex, currentClipIndex, composerControls } = ctrl;
    const clip = rows?.[currentRowIndex]?.clips?.[currentClipIndex];
    if (!clip) return null;

    // time currently shown (absolute)
    const abs = composerControls.playedSeconds > 0 ? composerControls.playedSeconds : customTime;
    const offset = Math.max(0, Math.min(abs - clip.time_start, clip.time_end - clip.time_start));
    return { clipId: clip.id, offset };
  }

  function findClipPosById(rows: TComposerClip[], id: string) {
    for (let r = 0; r < rows.length; r++) {
      const idx = rows[r].clips.findIndex((c) => c.id === id);
      if (idx !== -1) return { rowIndex: r, clipIndex: idx };
    }
    return null;
  }

  function restorePlayheadForClipId(rows: TComposerClip[], snapshot: { clipId: string; offset: number }, ctrl: typeof composerPlayControl) {
    const pos = findClipPosById(rows, snapshot.clipId);
    if (!pos) return;

    const clip = rows[pos.rowIndex].clips[pos.clipIndex];
    const newPlayed = clip.time_start + Math.max(0, Math.min(snapshot.offset, clip.time_end - clip.time_start));

    // order matters: first update rows in store, then marker/time
    dispatch(setComposerMarkerPosition(pos));
    dispatch(updateCustomTime(newPlayed));
    dispatch(
      updateComposerPlayControls({
        ...ctrl.composerControls,
        playedSeconds: newPlayed,
      }),
    );
  }

  const onDrop = async (result: DropResult) => {
    if (result.destination) {
      if (result.destination.droppableId === result.source.droppableId && result.destination.index === result.source.index) {
        return;
      }

      const canProceed = await ensureGeneratedMoviesWarningBeforeMutation();
      if (!canProceed) {
        return;
      }

      if (result.type === DROP_TYPE.TOPIC) {
        const snapshotActive = getActiveClipSnapshot(composerRows, composerPlayControl); // NEW

        const previousRows = JSON.parse(JSON.stringify(composerRows)) as TComposerClip[];
        const rows = arrayMove(composerRows, result.source.index, result.destination.index) as TComposerClip[];

        pushUndo({
          exec: async () => {
            const parsed = {
              topics: previousRows.map((t) => ({
                id: t.id,
                clips: t.clips.map((c, i) => ({ id: c.id, clipIndex: i })),
              })),
            };
            await patchReoderComposer(parsed, feedId!);
            dispatch(reorderComposerRows(previousRows));
            if (snapshotActive) restorePlayheadForClipId(previousRows, snapshotActive, composerPlayControl); // NEW
          },
        });

        dispatch(reorderComposerRows(rows));
        if (snapshotActive) restorePlayheadForClipId(rows, snapshotActive, composerPlayControl); // NEW
        setTimeout(() => reorderRows(rows), 250);
      } else if (result.type === DROP_TYPE.CLIP) {
        const snapshotActive = getActiveClipSnapshot(composerRows, composerPlayControl); // NEW

        /* ids ------------------------------------------------------------ */
        const [srcRowId, srcLineStr] = result.source.droppableId.split('-');
        const [dstRowId, dstLineStr] = result.destination!.droppableId.split('-');
        const srcLine = +srcLineStr;
        const dstLine = +dstLineStr;

        const srcRowIdx = composerRows.findIndex((r) => r.id === srcRowId);
        const dstRowIdx = composerRows.findIndex((r) => r.id === dstRowId);

        /* real indices ---------------------------------------------------- */
        const srcFlat = lineCoordToFlatIndex(
          composerRows[srcRowIdx].clips,
          markMargin,
          srcLine,
          result.source.index, // j-in-line
        );

        const dstFlat = lineCoordToFlatIndex(composerRows[dstRowIdx].clips, markMargin, dstLine, result.destination!.index);

        /* undo snapshot --------------------------------------------------- */
        const snapshotRows = JSON.parse(JSON.stringify(composerRows)) as TComposerClip[];
        pushUndo({
          exec: async () => {
            await patchReoderComposer(
              { topics: snapshotRows.map((t) => ({ id: t.id, clips: t.clips.map((c, i) => ({ id: c.id, clipIndex: i })) })) },
              feedId!,
            );
            dispatch(reorderComposerRows(snapshotRows));
            if (snapshotActive) restorePlayheadForClipId(snapshotRows, snapshotActive, composerPlayControl); // NEW
          },
        });

        /* multi-select move ---------------------------------------------- */
        if (currentSelectedComposerClips.length > 1) {
          const all = lodash.cloneDeep(composerRows);

          // remove every selected clip first
          currentSelectedComposerClips.forEach((c) => {
            const row = all[c.rowIndex!];
            const idx = row.clips.findIndex((k) => k.id === c.id);
            if (idx !== -1) row.clips.splice(idx, 1);
          });

          // deep-clone selected clips before inserting
          const toInsert = currentSelectedComposerClips.map((c) => lodash.cloneDeep(c));

          // insert at destination
          all[dstRowIdx].clips.splice(dstFlat, 0, ...toInsert);

          // refresh clipIndex and rowIndex on both rows
          [srcRowIdx, dstRowIdx].forEach((rIdx) => {
            all[rIdx].clips.forEach((clip, i) => {
              clip.clipIndex = i;
              clip.rowIndex = rIdx;
            });
          });

          dispatch(reorderComposerRows(all));
          if (snapshotActive) restorePlayheadForClipId(all, snapshotActive, composerPlayControl); // NEW
          setTimeout(() => reorderRows(all), 250);
          return;
        }

        /* single-clip move ------------------------------------------------ */
        const all = lodash.cloneDeep(composerRows);
        const [moved] = all[srcRowIdx].clips.splice(srcFlat, 1);
        all[dstRowIdx].clips.splice(dstFlat, 0, moved);

        // refresh clipIndex
        [all[srcRowIdx], all[dstRowIdx]].forEach((r) => r.clips.forEach((c, i) => (c.clipIndex = i)));

        dispatch(reorderComposerRows(all));
        if (snapshotActive) restorePlayheadForClipId(all, snapshotActive, composerPlayControl); // NEW
        setTimeout(() => reorderRows(all), 250);
      }
    }
  };

  const switchDownloadDataModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const handleDownloadClick = () => {
    setShowDownloadModal(true);
  };

  // DETECT IF SCROLL IS DONE BY TOUCHPAD OR MOUSEWHEEL = START

  const composerRef = useRef<HTMLDivElement>(null);
  const toggleMobileFollowPlayhead = () => {
    const nextFollowPlayhead = !followPlayhead;
    dispatch(toFollowPlayhead(nextFollowPlayhead));
    if (!nextFollowPlayhead) return;

    const scroller = composerRef.current;
    if (!scroller) return;

    scroller.scrollTo({
      left: Math.max(0, composerPlayControl.currentMarkerPosition - scroller.clientWidth / 2),
    });
  };

  const GAP = COMPOSER_CLIP_RIGHT_MARGIN ?? 12; // space between clips
  const GUTTER = 20; // padding in your layout
  const EPS = 1; // 1px safety buffer

  const calcZoomOutAllRows = useCallback((rows: TComposerClip[], width: number) => {
    if (!rows?.length || !width) return 0.2;
    let floor = Infinity;
    for (const r of rows) {
      const n = r.clips.length;
      if (!n) continue;
      const totalSec = r.clips.reduce((s, c) => s + (c.time_end - c.time_start), 0);
      if (totalSec <= 0) continue;
      const usable = Math.max(1, width - n * GAP - GUTTER - EPS);
      const mm = usable / totalSec;
      floor = Math.min(floor, mm);
    }
    return Number.isFinite(floor) ? Math.max(0.05, floor) : 0.2;
  }, []);

  const calcMaxZoomIn = useCallback((rows: TComposerClip[], width: number) => {
    if (!rows?.length || !width) return 4;
    let longest = 0;
    for (const r of rows) {
      for (const c of r.clips) longest = Math.max(longest, c.time_end - c.time_start);
    }
    if (longest <= 0) return 4;
    const usable = Math.max(1, width - GAP - GUTTER - EPS);
    return Math.max(0.1, usable / longest);
  }, []);

  useEffect(() => {
    const minM = calcZoomOutAllRows(composerRows as any, rowWidth || 0);
    const maxM = calcMaxZoomIn(composerRows as any, rowWidth || 0);
    setMinAutoMargin(minM);
    setMaxAutoMargin(maxM);
    if (markMargin < minM) dispatch(updateMarkMargin(minM));
    else if (markMargin > maxM) dispatch(updateMarkMargin(maxM));
  }, [composerRows, rowWidth, calcZoomOutAllRows, calcMaxZoomIn]);

  const autoZoom = useCallback(() => {
    if (composerRef.current) {
      const containerWidth = composerRef.current.clientWidth;
      const composerRowsElement = composerRef.current.querySelector(`.${styles.composerRows}`);
      if (composerRowsElement) {
        const currentClipLength = longestRowSeconds * markMargin;
        if (currentClipLength !== containerWidth) {
          const clipsGap = (longestRowClipsNumber + 1) * COMPOSER_CLIP_RIGHT_MARGIN + 20;
          const desiredMarkMargin = (containerWidth - clipsGap) / longestRowSeconds;
          if (Math.abs(desiredMarkMargin - markMargin) > 0.1) {
            const finalMarkMargin = desiredMarkMargin;
            setMaxMarkMargin(+finalMarkMargin);
            dispatch(updateMarkMargin(finalMarkMargin));
            autoZoomed = true;
          }
        }
      }
    }
  }, [longestRowSeconds, markMargin]);

  // useEffect(() => {
  //   if (composerRef.current && longestRowSeconds > 0 && !autoZoomed) {
  //     autoZoom();
  //   }
  // }, [composerRows, loading, feedId, longestRowSeconds, autoZoom]);

  const switchNotes = () => {
    dispatch(updateShowNotes(!showNotes));
  };

  const [startIndexForShiftSelect, setStartIndexForShiftSelect] = useState<{
    clipIndex: number;
    rowIndex: number;
  } | null>(null);

  const [playheadVisible, setPlayheadVisible] = useState(true);

  const selectClip = (
    clip: TClip,
    clipIndex: number,
    rowIndex: number,
    onDoubleClick?: boolean,
    clickEvt?: React.MouseEvent<HTMLDivElement>,
  ) => {
    const deselecting = !isHotkeyPressed('Shift') && (isHotkeyPressed('Ctrl') || isHotkeyPressed('Meta')) &&
      currentSelectedComposerClips.some((selected) => String(selected.id) === String(clip.id));
    if (!deselecting) enterClip(clip.id);
    const newTime = clickEvt
      ? (() => {
          const rect = (clickEvt.currentTarget as HTMLDivElement).getBoundingClientRect();
          const ratio = Math.max(0, Math.min(1, (clickEvt.clientX - rect.left) / rect.width));
          return clip.time_start + ratio * (clip.time_end - clip.time_start);
        })()
      : clip.time_start;

    dispatch(updateSecondaySelectedClips([]));
    if (isHotkeyPressed('shift')) {
      if (!startIndexForShiftSelect) {
        dispatch(updateSelectedMultipleClips(true));
        setStartIndexForShiftSelect({ clipIndex, rowIndex });
        const selectedClipsInRow = [{ ...clip, clipIndex, rowIndex }];
        dispatch(updateCustomTime(newTime));
        dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
        dispatch(updateCurrentSelectedComposerClipsKeepingRows(selectedClipsInRow));
      } else {
        dispatch(updateSelectedMultipleClips(true));
        let selectedClipsInRow: TClip[] = [];
        if (rowIndex > startIndexForShiftSelect.rowIndex) {
          for (let rowI = startIndexForShiftSelect.rowIndex; rowI <= rowIndex; rowI++) {
            const startIndexOfClip = rowI === startIndexForShiftSelect.rowIndex ? startIndexForShiftSelect.clipIndex : 0;
            const endIndexOfClip = rowI !== rowIndex ? composerRows[rowI].clips.length - 1 : clipIndex;
            selectedClipsInRow.push(...composerRows[rowI].clips.slice(startIndexOfClip, endIndexOfClip + 1));
          }
        } else if (rowIndex < startIndexForShiftSelect.rowIndex) {
          for (let rowI = startIndexForShiftSelect.rowIndex; rowI >= rowIndex; rowI--) {
            const endIndexOfClip =
              rowI === startIndexForShiftSelect.rowIndex ? startIndexForShiftSelect.clipIndex : composerRows[rowI].clips.length - 1;
            const startIndexOfClip = rowI === rowIndex ? clipIndex : 0;
            selectedClipsInRow.push(...composerRows[rowI].clips.slice(startIndexOfClip, endIndexOfClip + 1));
          }
        } else {
          const startIndexOfClip = clipIndex > startIndexForShiftSelect.clipIndex ? startIndexForShiftSelect.clipIndex : clipIndex;
          const endIndexOfClip = clipIndex === startIndexOfClip ? startIndexForShiftSelect.clipIndex : clipIndex;
          selectedClipsInRow = composerRows[rowIndex].clips.slice(startIndexOfClip, endIndexOfClip + 1);
        }
        const sortedSelectedComposerClips = lodash.cloneDeep([...currentSelectedComposerClips, ...selectedClipsInRow]);
        sortedSelectedComposerClips.sort((a, b) => {
          if (a.rowIndex !== b.rowIndex) {
            return a.rowIndex! - b.rowIndex!;
          } else {
            return a.clipIndex! - b.clipIndex!;
          }
        });
        let uniqueArr = getUniqueById(sortedSelectedComposerClips, 'id');
        dispatch(updateCustomTime(newTime));
        dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
        dispatch(updateCurrentSelectedComposerClipsKeepingRows(uniqueArr));
      }
      return;
    }

    if (isHotkeyPressed('Ctrl') || isHotkeyPressed('Meta')) {
      setPlayheadVisible(false);
      dispatch(toFollowPlayhead(false));
      setTimeout(() => setPlayheadVisible(true), 500);
      dispatch(updateSelectedMultipleClips(true));
      const sortedSelectedComposerClips = lodash.cloneDeep([...currentSelectedComposerClips, { ...clip, clipIndex, rowIndex }]);
      const notSortedSelectedComposerClips = lodash.cloneDeep([...currentSelectedComposerClips, { ...clip, clipIndex, rowIndex }]);
      sortedSelectedComposerClips.sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) {
          return a.rowIndex! - b.rowIndex!;
        } else {
          return a.clipIndex! - b.clipIndex!;
        }
      });

      let uniqueArr = getUniqueById(sortedSelectedComposerClips, 'id');
      const uniqueNotSortedArr = getUniqueById(notSortedSelectedComposerClips, 'id');
      const alreadyExistIndex = currentSelectedComposerClips.findIndex((c) => c.id === clip.id);
      if (alreadyExistIndex !== -1) {
        uniqueArr = uniqueArr.filter((c) => c.id !== clip.id);
        if (composerPlayControl.currentRowIndex === rowIndex && composerPlayControl.currentClipIndex === clipIndex) {
          let prevIndex = uniqueNotSortedArr.findIndex((c) => c.id === clip.id);
          if (prevIndex === 0) {
            prevIndex += 1;
          } else {
            prevIndex -= 1;
          }
          dispatch(
            setComposerMarkerPosition({
              rowIndex: uniqueNotSortedArr[prevIndex].rowIndex,
              clipIndex: uniqueNotSortedArr[prevIndex].clipIndex,
            }),
          );
          dispatch(updateCustomTime(uniqueNotSortedArr[prevIndex].time_start));
        }
      } else {
        dispatch(updateCustomTime(newTime));
        dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
      }
      dispatch(updateCurrentSelectedComposerClipsKeepingRows(uniqueArr));
      setStartIndexForShiftSelect({ clipIndex, rowIndex });
    } else if (onDoubleClick) {
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: true, playedSeconds: clip.time_start }));
      dispatch(updateCustomTime(clip.time_start));
      dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
      setStartIndexForShiftSelect({ clipIndex, rowIndex });
      dispatch(triggerRefresh());
    } else {
      dispatch(updateCustomTime(newTime));
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: newTime }));
      dispatch(triggerRefresh());
      dispatch(updateSelectedMultipleClips(false));
      dispatch(updateCurrentSelectedComposerClips([{ ...clip, clipIndex, rowIndex }]));
      dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
      setStartIndexForShiftSelect({ clipIndex, rowIndex });
    }

    setDynamicZoom(!dynamicZoom);
    dispatch(updateMarkMargin(dynamicZoom ? markMargin + 0.00001 : markMargin - 0.00001));
    dispatch(triggerRefresh());
  };

  const playButtonRef = useRef(null);

  useEffect(() => {
    // @ts-ignore
    const handleKeyDown = (event) => {
      // @ts-ignore
      if (event.keyCode === 32 && event.target.type !== 'text' && event.target.type !== 'textarea') {
        event.preventDefault();
        // @ts-ignore
        playButtonRef?.current?.click();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const disablePlayheadFollowing = useCallback(() => {
    if (followPlayhead) {
      dispatch(toFollowPlayhead(false));
    }
  }, [dispatch, followPlayhead]);

  const handleManualComposerMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.buttons === 1) {
        disablePlayheadFollowing();
      }
    },
    [disablePlayheadFollowing],
  );

  const disablePlayheadFollowerCallback = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      disablePlayheadFollowing();
    }
  };

  const [showSendClipModal, setShowSendClipModal] = useState(false);

  const switchSendClipModal = (open?: any) => {
    if (!canInteract) return;
    const willOpen = typeof open === 'boolean' ? open : !showSendClipModal;
    if (willOpen && composerPlayControl.composerControls.playing) {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: false,
        }),
      );
      notifyHidePauseOverlay();
    }
    setShowSendClipModal(willOpen);
  };

  const [showNewComposerModal, setNewComposerModal] = useState(false);
  const switchNewComposerModal = useCallback(() => {
    setNewComposerModal((prevShow) => {
      if (!prevShow && composerPlayControl.composerControls.playing) {
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playing: false,
          }),
        );
        notifyHidePauseOverlay();
      }
      return !prevShow;
    });
  }, [composerPlayControl.composerControls, dispatch]);

  const [skipConfirmationModals, setSkipConfirmationModals] = useState(false);
  const [showCreateOrSendModal, setShowCreateOrSendModal] = useState(false);
  const switchCreateOrSendModal = () => {
    if (!canInteract) return;
    const willOpen = !showCreateOrSendModal;
    if (willOpen && composerPlayControl.composerControls.playing) {
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: false,
        }),
      );
      notifyHidePauseOverlay();
    }
    setShowCreateOrSendModal(willOpen);
  };

  const [showDeleteClipModal, setShowDeleteClipModal] = useState(false);
  const switchShowDeleteComposerClipModal = () => {
    setShowDeleteClipModal(!showDeleteClipModal);
  };

  const [showAdditionalDeleteModal, setShowAdditionalDeleteModal] = useState(false);
  const switchAdditionalDeleteComposerModal = () => {
    setShowAdditionalDeleteModal(!showAdditionalDeleteModal);
  };

  const switchFullScreen = () => {
    if (!fullScreen.active) {
      fullScreen.enter();
    } else {
      fullScreen.exit();
    }
  };

  const speedOptionMenuRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(speedOptionMenuRef, (e) => {
    setTimeout(() => {
      dispatch(setShowSpeedOption(false));
    }, 300);
  });

  const switchSpeedOptionMenu = () => {
    dispatch(setShowSpeedOption(!showSpeedOption));
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const nextVolume = (newValue as number) / 100;
    rememberManualVolumeOverrideForFeed(feedId, nextVolume);
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        volume: nextVolume,
      }),
    );
  };
  const muteSwitch = () => {
    const nextVolume = composerPlayControl.composerControls.volume > 0 ? 0 : 1;
    rememberManualVolumeOverrideForFeed(feedId, nextVolume);
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        volume: nextVolume,
      }),
    );
  };
  const [showVolume, setShowVolume] = useState(false);
  const showVolumeSlider = () => {
    setShowVolume(true);
  };

  const closeVolumeSlider = () => {
    setShowVolume(false);
  };

  const [playingArrowStepSeconds, setPlayingArrowStepSeconds] = useState(
    getComposerWebPreferences().playingArrowStepSeconds,
  );

  useEffect(() => {
    setPlayingArrowStepSeconds(getComposerWebPreferences().playingArrowStepSeconds);
    return subscribeComposerWebPreferences((preferences) => {
      setPlayingArrowStepSeconds(preferences.playingArrowStepSeconds);
    });
  }, []);

  const [showCopyModal, setShowCopyModal] = useState(false);
  const switchCopyModal = () => {
    setShowCopyModal(!showCopyModal);
  };

  const SEEK_STEP = playingArrowStepSeconds;
  const frameFps = Number(videoData?.fps);
  const frameStep = 1 / (Number.isFinite(frameFps) && frameFps > 0 ? frameFps : 25);
  useHotkeys(
    'right',
    (e) => {
      e.preventDefault();
      if (composerPlayControl.isDragging) return;
      if (!composerPlayControl.composerControls.playing) {
        notifyHidePauseOverlay();
        const rows = composerRows;
        const r = composerPlayControl.currentRowIndex;
        const c = composerPlayControl.currentClipIndex;
        const active = rows?.[r]?.clips?.[c];
        const now =
          composerPlayControl.composerControls.playedSeconds > 0 ? composerPlayControl.composerControls.playedSeconds : customTime;
        let target = now + frameStep;

        if (clipDrawer) {
          const minTime = hasFixedClipBounds
            ? Number(clipDrawer.original_time_start ?? clipDrawer.time_start)
            : 0;
          const maxTime = hasFixedClipBounds
            ? Number(clipDrawer.original_time_end ?? clipDrawer.time_end)
            : clipEditorSourceDuration;
          target = Math.min(Math.max(now, minTime) + frameStep, maxTime);
        } else if (active) {
          const minTime = active.time_start;
          const maxTime = Math.max(active.time_start, active.time_end - frameStep);
          const clampedNow = Math.min(Math.max(now, minTime), maxTime);
          target = Math.min(clampedNow + frameStep, maxTime);
        }

        dispatch(updateCustomTime(target));
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playedSeconds: target,
          }),
        );
        return;
      }

      const rows = composerRows;
      const r = composerPlayControl.currentRowIndex;
      const c = composerPlayControl.currentClipIndex;
      const active = rows?.[r]?.clips?.[c];
      if (!active) return;

      const now = composerPlayControl.composerControls.playedSeconds > 0 ? composerPlayControl.composerControls.playedSeconds : customTime;

      const target = now + SEEK_STEP;

      // 1) simple in-clip seek
      if (target <= active.time_end - 0.001) {
        dispatch(updateCustomTime(target));
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playedSeconds: target,
          }),
        );
        // keep end-crossing tracker coherent
        if (typeof prevActiveIdRef !== 'undefined') {
          prevActiveIdRef.current = active.id;
          prevTimeRef.current = target;
        }
        return;
      }

      // 2) crossing clip end -> jump by selection rules
      let targetPos: { rowIndex: number; clipIndex: number } | null = null;

      if (currentSelectedComposerClips.length > 1) {
        // multi-select: next inside selection (keep selection)
        const nextId = getNextSelectedClipId(active.id, currentSelectedComposerClips);
        if (!nextId) return; // nothing to do at end of selection
        targetPos = findClipPosById(rows, nextId);
      } else {
        // single-select: next in visual order (AND select it)
        targetPos = getNextClipPosition(r, c, rows);
      }
      if (!targetPos) return;

      const nextClip = rows[targetPos.rowIndex].clips[targetPos.clipIndex];

      dispatch(setComposerMarkerPosition(targetPos));
      dispatch(updateCustomTime(nextClip.time_start));
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playedSeconds: nextClip.time_start,
        }),
      );

      if (currentSelectedComposerClips.length === 1) {
        dispatch(
          updateCurrentSelectedComposerClips([
            {
              ...nextClip,
              clipIndex: targetPos.clipIndex,
              rowIndex: targetPos.rowIndex,
              topicId: rows[targetPos.rowIndex].id,
              topicName: rows[targetPos.rowIndex].name,
            },
          ]),
        );
      }

      // reset end-crossing tracker to the new active
      if (typeof prevActiveIdRef !== 'undefined') {
        prevActiveIdRef.current = nextClip.id;
        prevTimeRef.current = nextClip.time_start;
      }
    },
    [
      dimmedIds,
      composerRows,
      composerPlayControl.currentRowIndex,
      composerPlayControl.currentClipIndex,
      composerPlayControl.composerControls,
      composerPlayControl.isDragging,
      currentSelectedComposerClips,
      clipDrawer,
      clipEditorSourceDuration,
      customTime,
      frameStep,
      hasFixedClipBounds,
      SEEK_STEP,
      dispatch,
    ],
  );

  useHotkeys(
    'left',
    (e) => {
      e.preventDefault();
      if (composerPlayControl.isDragging) return;
      if (!composerPlayControl.composerControls.playing) {
        notifyHidePauseOverlay();
        const rows = composerRows;
        const r = composerPlayControl.currentRowIndex;
        const c = composerPlayControl.currentClipIndex;
        const active = rows?.[r]?.clips?.[c];
        const now =
          composerPlayControl.composerControls.playedSeconds > 0 ? composerPlayControl.composerControls.playedSeconds : customTime;
        let target = Math.max(0, now - frameStep);

        if (clipDrawer) {
          const minTime = hasFixedClipBounds
            ? Number(clipDrawer.original_time_start ?? clipDrawer.time_start)
            : 0;
          const maxTime = hasFixedClipBounds
            ? Number(clipDrawer.original_time_end ?? clipDrawer.time_end)
            : clipEditorSourceDuration;
          target = Math.max(Math.min(now, maxTime) - frameStep, minTime);
        } else if (active) {
          const minTime = active.time_start;
          const maxTime = Math.max(active.time_start, active.time_end - frameStep);
          const clampedNow = Math.min(Math.max(now, minTime), maxTime);
          target = Math.max(minTime, clampedNow - frameStep);
        }

        dispatch(updateCustomTime(target));
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playedSeconds: target,
          }),
        );
        return;
      }

      const rows = composerRows;
      const r = composerPlayControl.currentRowIndex;
      const c = composerPlayControl.currentClipIndex;
      const active = rows?.[r]?.clips?.[c];
      if (!active) return;

      const now = composerPlayControl.composerControls.playedSeconds > 0 ? composerPlayControl.composerControls.playedSeconds : customTime;

      const target = now - SEEK_STEP;

      // 1) simple in-clip seek
      if (target >= active.time_start + 0.001) {
        dispatch(updateCustomTime(target));
        dispatch(
          updateComposerPlayControls({
            ...composerPlayControl.composerControls,
            playedSeconds: target,
          }),
        );
        // keep end-crossing tracker coherent
        if (typeof prevActiveIdRef !== 'undefined') {
          prevActiveIdRef.current = active.id;
          prevTimeRef.current = target;
        }
        return;
      }

      // 2) crossing clip start -> jump by selection rules
      let targetPos: { rowIndex: number; clipIndex: number } | null = null;

      if (currentSelectedComposerClips.length > 1) {
        // multi-select: previous inside selection (keep selection)
        const prevId = getPrevSelectedClipId(active.id, currentSelectedComposerClips, dimmedIds);
        if (!prevId) return; // nothing before in selection
        targetPos = findClipPosById(rows, prevId);
      } else {
        // single-select: previous in visual order (AND select it)
        targetPos = getPrevClipPosition(r, c, rows, dimmedIds);
      }
      if (!targetPos) return;

      const prevClip = rows[targetPos.rowIndex].clips[targetPos.clipIndex];

      dispatch(setComposerMarkerPosition(targetPos));
      dispatch(updateCustomTime(prevClip.time_start));
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playedSeconds: prevClip.time_start,
        }),
      );

      if (currentSelectedComposerClips.length === 1) {
        dispatch(
          updateCurrentSelectedComposerClips([
            {
              ...prevClip,
              clipIndex: targetPos.clipIndex,
              rowIndex: targetPos.rowIndex,
              topicId: rows[targetPos.rowIndex].id,
              topicName: rows[targetPos.rowIndex].name,
            },
          ]),
        );
      }

      // reset end-crossing tracker to the new active
      if (typeof prevActiveIdRef !== 'undefined') {
        prevActiveIdRef.current = prevClip.id;
        prevTimeRef.current = prevClip.time_start;
      }
    },
    [
      dimmedIds,
      composerRows,
      composerPlayControl.currentRowIndex,
      composerPlayControl.currentClipIndex,
      composerPlayControl.composerControls,
      composerPlayControl.isDragging,
      currentSelectedComposerClips,
      clipDrawer,
      clipEditorSourceDuration,
      customTime,
      frameStep,
      hasFixedClipBounds,
      SEEK_STEP,
      dispatch,
    ],
  );

  useLayoutEffect(() => {
    const modalEl = document.getElementById('clip-drawer-modal');
    const composerArea = composerRef.current;
    if (!clipDrawer || !modalEl || !composerArea) return;

    const updatePosition = () => {
      // Keep the draggable offset separate from the default position.
      const area = composerArea.getBoundingClientRect();
      const composerTop = composerArea.parentElement?.getBoundingClientRect().top ?? area.top;
      const preferredTop = Math.max(composerTop + 8, area.top + 8 - 70);
      const top = Math.max(8, Math.min(preferredTop, window.innerHeight - modalEl.offsetHeight - 8));
      modalEl.style.top = `${top - area.top}px`;
      modalEl.style.left = `${Math.max(8, (area.width - modalEl.offsetWidth) / 2)}px`;
    };
    updatePosition();

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const schedulePositionUpdate = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updatePosition, 150);
    };
    const observer = new ResizeObserver(schedulePositionUpdate);
    observer.observe(composerArea);
    observer.observe(modalEl);
    window.addEventListener('resize', schedulePositionUpdate);
    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
      window.removeEventListener('resize', schedulePositionUpdate);
    };
  }, [clipDrawer?.id, clipDrawer?.topicId, hasOriginalClipBounds, canTrimClipToPlayhead, videoPlayerHeight]);

  useEffect(() => {
    if (showColorPicker) {
      const element = document.querySelector('.w-color-interactive.w-color-saturation');
      if (element instanceof HTMLElement) {
        element.click();
      }
    }
  }, [showColorPicker]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, clipDrawer]);

  const clipDrawerRef = useRef<HTMLDivElement | null>(null);
  const clipColorButtonRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(clipDrawerRef, (event) => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('[data-composer-playhead-control="true"], [data-composer-resize-control="true"], [data-preserve-clip-edit]')
    ) return;
    if (clipDrawer?.isEdit) {
      cancelClipEditing();
    }
  });

  useEffect(() => {
    if (!clipDrawer) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelClipEditing();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [cancelClipEditing, clipDrawer]);

  const [parentFolder, setParentFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>();
  const folderId = findSearchValue('folderId');

  const fetchParentFolder = async () => {
    const res = await getFolderById(folderId!);
    if (res.data) {
      setParentFolder(res.data);
    }
  };

  useEffect(() => {
    if (folderId) {
      fetchParentFolder();
    }
  }, [folderId]);

  const isCollaborator =
    parentFolder?.collaborators.some((u) => u.email === currentUser?.email) ||
    !!parentFolder?.group_collaborators.find((g) => g.analysts.find((u) => u.email === currentUser?.email));

  const isMyComposer = composerData?.type === VIDEOFEED_TYPE.MY_COMPOSER;
  const showCopy = !isMyComposer && !!computedQuality;
  const isOwner = currentUser?.id === composerData?.created_by?.id;
  const access = isContentAdminOA ? ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE : composerData?.highest_user_access?.access?.title;
  const isSharedComposer = !!composerData && composerData?.created_by?.id !== currentUser?.id;
  const isReadOnlyShared = isSharedComposer && (access === ACCESS_TYPE_ENUM.VIEW || access === ACCESS_TYPE_ENUM.VIEW_AND_COMMENT);
  const canEdit = !isReadOnlyShared && (isOwner || access === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE || isCollaborator);
  const canCommentAndCreate = !isReadOnlyShared && access === ACCESS_TYPE_ENUM.COMMENT_AND_CREATE;
  const canCommentCreateAndReshare = !isReadOnlyShared && access === ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE;
  const canCreateComposerFromContent =
    !isReadOnlyShared &&
    (isOwner ||
      isCollaborator ||
      access === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE ||
      access === ACCESS_TYPE_ENUM.COMMENT_AND_CREATE ||
      access === ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE);
  const canInteract = !isReadOnlyShared && (isOwner || canEdit || canCommentAndCreate || canCommentCreateAndReshare || isCollaborator);
  const canInteractMyComposer =
    !isReadOnlyShared &&
    (currentUser?.id === composerData?.created_by?.id || canEdit || canCommentAndCreate || canCommentCreateAndReshare || isCollaborator);
  const canShowCreateAndSendButtons = !isReadOnlyShared && (canInteract || isMyComposer);
  const sidebarPlayback = useSidebarPlayback({
    activeClip: composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex],
    seconds: composerPlayControl.composerControls.playedSeconds > 0 ? composerPlayControl.composerControls.playedSeconds : customTime,
    playing: composerPlayControl.composerControls.playing,
    seeking: composerPlayControl.isDragging || composerPlayControl.composerControls.seeking,
    onTimelineEnded: filterActive ? (time) => {
      const active = composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex];
      if (!active || !composerPlayControl.composerControls.playing || composerPlayControl.isDragging ||
        time < active.time_end - 0.05) return;
      dispatch(updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: Math.max(time, active.time_end),
      }));
    } : undefined,
  });
  const focusTimeline = useFavouriteEditor({
    rows: composerRows,
    activeClipId: composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex]?.id,
    select: (clip, additive) => {
      const target = composerRows[clip.rowIndex!]?.clips[clip.clipIndex!];
      if (!target) return;
      dispatch(updateComposerRowSelection({
        selectedClips: [...(additive ? currentSelectedComposerClips : []), {
          ...target, rowIndex: clip.rowIndex!, clipIndex: clip.clipIndex!, topicId: clip.topicId!, topicName: clip.topicName!,
        }].sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0) || (a.clipIndex ?? 0) - (b.clipIndex ?? 0)),
        selectedRows: additive ? currentSelectedComposerRows : [],
        lastSelectedRowId: '',
      }));
    },
    deselect: (clips) => {
      const removed = Array.isArray(clips) ? clips : [clips];
      const clipIds = new Set(removed.map((clip) => String(clip.id)));
      const topicIds = new Set(removed.map((clip) => String(clip.topicId)));
      dispatch(updateComposerRowSelection({
        selectedClips: currentSelectedComposerClips.filter((selected) => !clipIds.has(String(selected.id))),
        selectedRows: currentSelectedComposerRows.filter((row) => !topicIds.has(String(row.id))),
        lastSelectedRowId: topicIds.has(lastSelectedComposerRowId) ? '' : lastSelectedComposerRowId,
      }));
    },
    canEdit: !!canEdit,
    canSend: !!canInteract,
    isPlayheadOverClip: (clip) => {
      const active = composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex];
      const seconds = composerPlayControl.composerControls.playedSeconds;
      return String(active?.id) === String(clip.id) && seconds >= clip.time_start && seconds <= clip.time_end;
    },
    canLockAngle: (clip) => !!currentVideoThatInFrame && (clip.videos?.length ? clip.videos : currentComposerVideos)
      .some((video) => String(video.id) === String(currentVideoThatInFrame.id)),
    setAngleLock: async (clip, locked_angle_id) => {
      if (!canEdit) throw new Error('Clip is read only');
      await setClipAngleLock(clip.id, locked_angle_id);
      dispatch(updateComposerClipAngleLock({ clipId: clip.id, locked_angle_id }));
    },
    pause: pauseComposerPlayback,
    edit: (clip) => {
      if (!canEdit) return;
      const target = composerRows[clip.rowIndex!]?.clips[clip.clipIndex!];
      if (!target) return;
      selectClip(target, clip.clipIndex!, clip.rowIndex!);
      handleEditClip(clip);
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false, playedSeconds: clip.time_start }));
    },
    seek: (clip, playing, seconds = clip.time_start) => {
      const target = composerRows[clip.rowIndex!]?.clips[clip.clipIndex!];
      if (!target) return;
      enterClip(clip.id);
      dispatch(toFollowPlayhead(true));
      dispatch(updateCustomTime(seconds));
      dispatch(setComposerMarkerPosition({ rowIndex: clip.rowIndex!, clipIndex: clip.clipIndex! }));
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing, playedSeconds: seconds }));
      dispatch(triggerRefresh());
    },
    send: (clips) => {
      if (!canInteract || !clips.length) return;
      dispatch(updateOrderedSecondarySelectedClips(clips));
      setSkipConfirmationModals(false);
      switchSendClipModal(true);
    },
  });
  const openSendFromSelection = useCallback(() => {
    if (isReadOnlyShared) return;
    if (!isMyComposer && !canInteract) return;
    if (!currentSelectedComposerClips.length) return;

    if (composerData?.type !== VIDEOFEED_TYPE.MY_COMPOSER) {
      switchCreateOrSendModal();
    } else {
      switchSendClipModal();
    }
  }, [
    isReadOnlyShared,
    isMyComposer,
    canInteract,
    currentSelectedComposerClips.length,
    composerData?.type,
    switchCreateOrSendModal,
    switchSendClipModal,
  ]);

  useHotkeys(
    ['s', 'Shift + Control + V', 'Shift + Meta + V'],
    (event) => {
      if (event.repeat) return;
      openSendFromSelection();
    },
    { ignoreEventWhen: ignoreSendClipShortcut },
    [openSendFromSelection],
  );

  const [canDragRow, setCanDragRow] = useState(false);
  const [canDragClip, setCanDragClip] = useState(false);

  useEffect(() => {
    if (userAccess === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE || canEdit) {
      setCanDragRow(true);
    } else if (currentUser?.id === composerData?.created_by?.id || canEdit) {
      setCanDragRow(true);
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (userAccess === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE || canEdit) {
        if (event.ctrlKey || event.metaKey) {
          setCanDragClip(true);
        }
      } else if (currentUser?.id === composerData?.created_by?.id || canEdit) {
        if (event.ctrlKey || event.metaKey) {
          setCanDragClip(true);
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        setCanDragClip(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [userAccess, parentFolder]);

  const [isDraggingClip, setIsDraggingClip] = useState(false);

  // Step 1 helper: find next clip in visual order
  function getNextClipPosition(r: number, c: number, rows: TComposerClip[]) {
    for (let rr = r; rr < rows.length; rr++) {
      const clips = rows[rr]?.clips || [];
      for (let cc = rr === r ? c + 1 : 0; cc < clips.length; cc++) {
        if (!dimmedIds.has(String(clips[cc].id))) return { rowIndex: rr, clipIndex: cc };
      }
    }
    return null;
  }

  // Step 2 helper: next selected id (order = currentSelectedComposerClips as-is)
  function getNextSelectedClipId(currentId: string, selected: TClip[]) {
    const i = selected.findIndex((c) => c.id === currentId);
    return i === -1 ? null : selected.slice(i + 1).find((clip) => !dimmedIds.has(String(clip.id)))?.id ?? null;
  }

  // You already have this elsewhere; include if not present:
  // function findClipPosById(rows: TComposerClip[], id: string) { ... }
  const advanceLock = useRef(false);

  // for restart-on-play (Step 3)
  const wasPlayingRef = useRef(false);

  // end-crossing guard to avoid false jumps on selection changes
  const prevTimeRef = useRef(0);
  const prevActiveIdRef = useRef<string | null>(null);

  // helper to require actual boundary crossing on the same active clip
  function crossedClipEnd(now: number, active: { id: string; time_end: number }) {
    const end = active.time_end - 0.05; // time epsilon
    const crossed = prevActiveIdRef.current === active.id ? prevTimeRef.current < end && now >= end : false;
    // update trackers
    prevActiveIdRef.current = active.id;
    prevTimeRef.current = now;
    return crossed;
  }
  // Step 3: If paused at the end, pressing Play should jump to the first clip
  useEffect(() => {
    const wasPlaying = wasPlayingRef.current;
    const playing = composerPlayControl.composerControls.playing;

    // rising edge: paused -> playing
    const resumed = !wasPlaying && playing;

    if (!sidebarPlayback.enabled && resumed && !composerPlayControl.isDragging) {
      const r = composerPlayControl.currentRowIndex;
      const c = composerPlayControl.currentClipIndex;
      const active = composerRows?.[r]?.clips?.[c];

      if (active) {
        const now =
          composerPlayControl.composerControls.playedSeconds > 0 ? composerPlayControl.composerControls.playedSeconds : customTime;

        if (currentSelectedComposerClips.length > 1) {
          // MULTI-SELECT: if parked at end of last selected -> jump to first selected
          const isLastSelected = currentSelectedComposerClips.some((clip) => clip.id === active.id) &&
            !getNextSelectedClipId(active.id, currentSelectedComposerClips);
          if (isLastSelected && now >= active.time_end - 0.05) {
            const firstSelected = currentSelectedComposerClips.find((clip) => !dimmedIds.has(String(clip.id)));
            const firstPos = firstSelected ? findClipPosById(composerRows, firstSelected.id) : null;
            if (firstSelected && firstPos) {
              enterClip(firstSelected.id);
              dispatch(setComposerMarkerPosition(firstPos));
              dispatch(updateCustomTime(firstSelected.time_start));
              dispatch(
                updateComposerPlayControls({
                  ...composerPlayControl.composerControls,
                  playedSeconds: firstSelected.time_start,
                  playing: true,
                }),
              );
              // reset end-crossing tracker to new clip
              prevActiveIdRef.current = firstSelected.id;
              prevTimeRef.current = firstSelected.time_start;
            } else {
              dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }));
            }
          }
        } else if (currentSelectedComposerClips.length === 1 || filterActive) {
          // SINGLE-SELECT: if parked at end of last clip overall -> jump to very first clip overall
          const isLastOverall = !getNextClipPosition(r, c, composerRows);
          if (isLastOverall && now >= active.time_end - 0.05) {
            const firstPos = getNextClipPosition(0, -1, composerRows);
            if (firstPos) {
              const firstClip = composerRows[firstPos.rowIndex].clips[firstPos.clipIndex];
              enterClip(firstClip.id);
              dispatch(setComposerMarkerPosition(firstPos));
              dispatch(updateCustomTime(firstClip.time_start));
              if (currentSelectedComposerClips.length === 1) dispatch(
                updateCurrentSelectedComposerClips([
                  {
                    ...firstClip,
                    clipIndex: firstPos.clipIndex,
                    rowIndex: firstPos.rowIndex,
                    topicId: composerRows[firstPos.rowIndex].id,
                    topicName: composerRows[firstPos.rowIndex].name,
                  },
                ]),
              );
              dispatch(
                updateComposerPlayControls({
                  ...composerPlayControl.composerControls,
                  playedSeconds: firstClip.time_start,
                  playing: true,
                }),
              );
              // reset end-crossing tracker to new clip
              prevActiveIdRef.current = firstClip.id;
              prevTimeRef.current = firstClip.time_start;
            } else {
              dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }));
            }
          }
        }
      }
    }

    wasPlayingRef.current = playing;
  }, [
    enterClip,
    sidebarPlayback.enabled,
    filterActive,
    dimmedIds,
    composerPlayControl.composerControls.playing,
    composerPlayControl.composerControls.playedSeconds,
    composerPlayControl.currentClipIndex,
    composerPlayControl.currentRowIndex,
    composerPlayControl.isDragging,
    currentSelectedComposerClips,
    composerRows,
    customTime,
    dispatch,
  ]);

  useEffect(() => {
    const { composerControls, currentRowIndex, currentClipIndex, isDragging } = composerPlayControl;
    const { playing, playedSeconds } = composerControls;

    const selectionCount = currentSelectedComposerClips.length;
    const isSingleSelect = selectionCount === 1;
    const isMultiSelect = selectionCount > 1;

    // No selection or not playing / dragging → nothing to do
    if (sidebarPlayback.enabled || !playing || isDragging || (!isSingleSelect && !isMultiSelect && !filterActive)) {
      advanceLock.current = false;
      return;
    }

    const r = currentRowIndex;
    const c = currentClipIndex;
    const active = composerRows?.[r]?.clips?.[c];
    if (!active) return;

    const now = playedSeconds > 0 ? playedSeconds : customTime;

    // Only advance when we truly cross the end of THIS clip
    if (!crossedClipEnd(now, active)) {
      advanceLock.current = false;
      return;
    }

    if (advanceLock.current) return;
    advanceLock.current = true;

    let nextPos: { rowIndex: number; clipIndex: number } | null = null;

    if (isMultiSelect) {
      // MULTI-SELECT: go to next selected clip
      const nextId = getNextSelectedClipId(active.id, currentSelectedComposerClips);
      if (!nextId) {
        // no more selected clips → stop
        dispatch(updateComposerPlayControls({ ...composerControls, playing: false }));
        advanceLock.current = false;
        return;
      }

      const pos = findClipPosById(composerRows, nextId);
      if (!pos) {
        dispatch(updateComposerPlayControls({ ...composerControls, playing: false }));
        advanceLock.current = false;
        return;
      }

      nextPos = pos;
    } else {
      // SINGLE-SELECT: go to next clip overall
      const pos = getNextClipPosition(r, c, composerRows);
      if (!pos) {
        // last clip overall → stop (loop-on-resume handled by the first effect)
        dispatch(updateComposerPlayControls({ ...composerControls, playing: false }));
        advanceLock.current = false;
        return;
      }

      nextPos = pos;
    }

    if (!nextPos) {
      advanceLock.current = false;
      return;
    }

    const nextClip = composerRows[nextPos.rowIndex].clips[nextPos.clipIndex];

    dispatch(setComposerMarkerPosition(nextPos));
    dispatch(updateCustomTime(nextClip.time_start));

    // For single-select we keep the one selected clip in sync with the active clip
    if (isSingleSelect) {
      const topicRow = composerRows[nextPos.rowIndex];
      dispatch(
        updateCurrentSelectedComposerClips([
          {
            ...nextClip,
            clipIndex: nextPos.clipIndex,
            rowIndex: nextPos.rowIndex,
            topicId: topicRow.id,
            topicName: topicRow.name,
          },
        ]),
      );
    }

    dispatch(
      updateComposerPlayControls({
        ...composerControls,
        playedSeconds: nextClip.time_start,
        playing: true,
      }),
    );

    // reset crossing tracker for the new active
    prevActiveIdRef.current = nextClip.id;
    prevTimeRef.current = nextClip.time_start;

    // unlock in next tick to avoid double-fire in same render cycle
    setTimeout(() => {
      advanceLock.current = false;
    }, 0);
  }, [
    sidebarPlayback.enabled,
    filterActive,
    dimmedIds,
    composerPlayControl.composerControls.playedSeconds,
    composerPlayControl.composerControls.playing,
    composerPlayControl.currentClipIndex,
    composerPlayControl.currentRowIndex,
    composerPlayControl.isDragging,
    currentSelectedComposerClips, // order matters for multi
    composerRows,
    customTime,
    dispatch,
  ]);

  const handleNextComposerClip = () => {
    if (sidebarPlayback.next()) return;
    const rows = composerRows;
    const r = composerPlayControl.currentRowIndex;
    const c = composerPlayControl.currentClipIndex;
    const active = rows?.[r]?.clips?.[c];
    if (!active) return;

    let targetPos: { rowIndex: number; clipIndex: number } | null = null;

    if (currentSelectedComposerClips.length > 1) {
      const nextId = getNextSelectedClipId(active.id, currentSelectedComposerClips);
      targetPos = nextId ? findClipPosById(rows, nextId) : null;
    } else {
      targetPos = getNextClipPosition(r, c, rows);
    }
    if (!targetPos) {
      if (filterActive) pauseComposerPlayback();
      return;
    }

    const nextClip = rows[targetPos.rowIndex].clips[targetPos.clipIndex];
    enterClip(nextClip.id);
    dispatch(setComposerMarkerPosition(targetPos));
    dispatch(updateCustomTime(nextClip.time_start));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: nextClip.time_start,
      }),
    );

    if (currentSelectedComposerClips.length === 1) {
      dispatch(
        updateCurrentSelectedComposerClips([
          {
            ...nextClip,
            clipIndex: targetPos.clipIndex,
            rowIndex: targetPos.rowIndex,
            topicId: rows[targetPos.rowIndex].id,
            topicName: rows[targetPos.rowIndex].name,
          },
        ]),
      );
    }

    prevActiveIdRef.current = nextClip.id;
    prevTimeRef.current = nextClip.time_start;
  };

  const handlePreviousComposerClip = () => {
    if (sidebarPlayback.previous()) return;
    const rows = composerRows;
    const r = composerPlayControl.currentRowIndex;
    const c = composerPlayControl.currentClipIndex;
    const active = rows?.[r]?.clips?.[c];
    if (!active) return;

    const now = composerPlayControl.composerControls.playedSeconds ?? 0;
    if (!dimmedIds.has(String(active.id)) && now > active.time_start + 0.5) {
      enterClip(active.id);
      const nudge = (Math.random() < 0.5 ? -1 : 1) * 1e-10;
      dispatch(setComposerMarkerPosition({ rowIndex: r, clipIndex: c }));
      dispatch(updateCustomTime(Math.max(0, active.time_start + nudge)));
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playedSeconds: active.time_start,
        }),
      );
      prevActiveIdRef.current = active.id;
      prevTimeRef.current = active.time_start;
      return;
    }

    let targetPos: { rowIndex: number; clipIndex: number } | null = null;
    if (currentSelectedComposerClips.length > 1) {
      const prevId = getPrevSelectedClipId(active.id, currentSelectedComposerClips, dimmedIds);
      targetPos = prevId ? findClipPosById(rows, prevId) : null;
    } else {
      targetPos = getPrevClipPosition(r, c, rows, dimmedIds);
    }
    if (!targetPos) {
      if (filterActive) pauseComposerPlayback();
      return;
    }

    const prevClip = rows[targetPos.rowIndex].clips[targetPos.clipIndex];
    enterClip(prevClip.id);
    const nudge = (Math.random() < 0.5 ? -1 : 1) * 1e-10;
    dispatch(setComposerMarkerPosition(targetPos));
    dispatch(updateCustomTime(Math.max(0, prevClip.time_start + nudge)));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: prevClip.time_start,
      }),
    );

    if (currentSelectedComposerClips.length === 1) {
      dispatch(
        updateCurrentSelectedComposerClips([
          {
            ...prevClip,
            clipIndex: targetPos.clipIndex,
            rowIndex: targetPos.rowIndex,
            topicId: rows[targetPos.rowIndex].id,
            topicName: rows[targetPos.rowIndex].name,
          },
        ]),
      );
    }

    prevActiveIdRef.current = prevClip.id;
    prevTimeRef.current = prevClip.time_start;
  };

  useHotkeys(
    'tab',
    (e) => {
      if ((e.target as HTMLElement | null)?.closest('[data-clip-filter-panel]')) return;
      e.preventDefault();
      handleNextComposerClip();
    },
    [
      sidebarPlayback.next,
      enterClip,
      filterActive,
      pauseComposerPlayback,
      dimmedIds,
      composerRows,
      composerPlayControl.currentRowIndex,
      composerPlayControl.currentClipIndex,
      composerPlayControl.composerControls,
      currentSelectedComposerClips,
      dispatch,
    ],
  );

  useHotkeys(
    'shift+tab',
    (e) => {
      if ((e.target as HTMLElement | null)?.closest('[data-clip-filter-panel]')) return;
      e.preventDefault();
      handlePreviousComposerClip();
    },
    [
      sidebarPlayback.previous,
      enterClip,
      filterActive,
      pauseComposerPlayback,
      dimmedIds,
      composerRows,
      composerPlayControl.currentRowIndex,
      composerPlayControl.currentClipIndex,
      composerPlayControl.composerControls,
      currentSelectedComposerClips,
      dispatch,
    ],
  );

  const handleMobileComposerSeek = (positionInSeconds: number) => {
    const boundedPosition = Math.min(Math.max(positionInSeconds, 0), composerPlayControl.composerVideoLength);
    let accumulatedTime = 0;

    for (let rowIndex = 0; rowIndex < composerRows.length; rowIndex++) {
      const row = composerRows[rowIndex];
      for (let clipIndex = 0; clipIndex < row.clips.length; clipIndex++) {
        const clip = row.clips[clipIndex];
        const clipDuration = clip.time_end - clip.time_start;

        if (accumulatedTime + clipDuration >= boundedPosition) {
          const clipTime = Math.min(clip.time_start + boundedPosition - accumulatedTime, clip.time_end);
          dispatch(setComposerMarkerPosition({ rowIndex, clipIndex }));
          dispatch(updateCustomTime(clipTime));
          dispatch(
            updateComposerPlayControls({
              ...composerPlayControl.composerControls,
              playedSeconds: clipTime,
            }),
          );
          dispatch(updateSelectedMultipleClips(false));
          dispatch(updateSecondaySelectedClips([]));
          dispatch(
            updateCurrentSelectedComposerClips([
              {
                ...clip,
                rowIndex,
                clipIndex,
                topicId: row.id,
                topicName: row.name,
              },
            ]),
          );
          dispatch(triggerRefresh());
          return;
        }

        accumulatedTime += clipDuration;
      }
    }
  };

  const handleMobileComposerDeselect = () => {
    dispatch(updateComposerRowSelection({ selectedRows: [], selectedClips: [], lastSelectedRowId: '' }));
    dispatch(updateSecondaySelectedClips([]));
  };

  const selectedFilteredClipCount = currentSelectedComposerClips.filter((clip) => !dimmedIds.has(String(clip.id))).length;
  const previousFilterConfig = useRef(clipFilterConfig);
  const previousDimmedIds = useRef(dimmedIds);
  useEffect(() => {
    if (waitingForComments) return;
    const submitted = previousFilterConfig.current !== clipFilterConfig;
    if (!submitted && previousDimmedIds.current === dimmedIds) return;
    previousFilterConfig.current = clipFilterConfig;
    previousDimmedIds.current = dimmedIds;
    if (!filterActive) return;
    if (submitted) {
      const clips = composerRows.flatMap((row, rowIndex) => row.clips.flatMap((clip, clipIndex) =>
        dimmedIds.has(String(clip.id)) ? [] : [{ ...clip, rowIndex, clipIndex, topicId: row.id, topicName: row.name }],
      ));
      dispatch(updateComposerRowSelection({ selectedRows: [], selectedClips: clips, lastSelectedRowId: '' }));
      dispatch(updateSecondaySelectedClips([]));
      return;
    }
    const selectedClips = currentSelectedComposerClips.filter((clip) => !dimmedIds.has(String(clip.id)));
    if (selectedClips.length === currentSelectedComposerClips.length) return;
    const rowIds = new Set(selectedClips.map((clip) => String(clip.topicId)));
    dispatch(updateComposerRowSelection({
      selectedRows: currentSelectedComposerRows.filter((row) => rowIds.has(String(row.id))),
      selectedClips,
      lastSelectedRowId: rowIds.has(String(lastSelectedComposerRowId)) ? lastSelectedComposerRowId : '',
    }));
  }, [clipFilterConfig, composerRows, dimmedIds, filterActive, waitingForComments, currentSelectedComposerClips, currentSelectedComposerRows, lastSelectedComposerRowId, dispatch]);

  const hasClipSelection = currentSelectedComposerClips.length > 0 || currentSelectedComposerRows.length > 0;
  const filteredClips = useMemo(() => filterActive ? composerRows.flatMap((row, rowIndex) =>
    row.clips.flatMap((clip, clipIndex) => dimmedIds.has(String(clip.id)) ? [] : [{
      ...clip, rowIndex, clipIndex, topicId: row.id, topicName: row.name,
    }]),
  ) : [], [filterActive, composerRows, dimmedIds]);

  const selectFilteredClips = () => {
    if (!filterActive || waitingForComments || !filteredClips.length) return;
    setWorkspace('timeline');
    dispatch(updateComposerRowSelection({ selectedRows: [], selectedClips: filteredClips, lastSelectedRowId: '' }));
    dispatch(updateSecondaySelectedClips([]));
    setStartIndexForShiftSelect(null);

    const active = composerRows[composerPlayControl.currentRowIndex]?.clips[composerPlayControl.currentClipIndex];
    if (active && filteredClips.some((clip) => String(clip.id) === String(active.id))) return;

    const first = filteredClips[0];
    enterClip(first.id);
    dispatch(setComposerMarkerPosition({ rowIndex: first.rowIndex, clipIndex: first.clipIndex }));
    dispatch(updateCustomTime(first.time_start));
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: first.time_start }));
    prevActiveIdRef.current = first.id;
    prevTimeRef.current = first.time_start;
    dispatch(triggerRefresh());
  };

  const organizationHaveDownloadHours = Number(myOrganization?.download_hours_limit || 0) > 0;

  const currentOrgUser =
    currentUser?.organization_users?.find((orgUser) => orgUser.organization?.id === myOrganization?.id) ||
    currentUser?.organization_users?.[0];
  const allowDownloadOwnComposers = currentOrgUser?.allow_download_own_composers ?? true;
  const isViewerOrAnalyst = [RolesLowerCase.VIEWER, RolesLowerCase.ANALYST].includes(currentUser?.role?.title as RolesLowerCase);
  const isOwnMyComposer = composerData?.type === VIDEOFEED_TYPE.MY_COMPOSER && composerData?.created_by?.id === currentUser?.id;
  const canDownloadOwnMyComposer = !isViewerOrAnalyst || allowDownloadOwnComposers;

  const isComposerShared = composerData?.created_by?.id !== currentUser?.id;
  const disableDownloadInSharedComposer = isComposerShared && !composerData?.highest_user_access?.allow_downloads;

  const openComposerDownload = () => {
    if (!isMyComposer || !feedId) return;
    if (isOwnMyComposer && !canDownloadOwnMyComposer) return;
    acceptedGeneratedMoviesWarningRef.current[String(feedId)] = false;
    dispatch(
      openDownloadManager({
        feedId: String(feedId),
        feedTitle: composerData.title,
      }),
    );
  };
  const prefix = findSearchValue('prefix');
  const mode = findSearchValue('mode');
  useEffect(() => {
    if (feedId && prefix && mode) {
      openComposerDownload();
    }
  }, [prefix, mode, isMyComposer, feedId]);

  return (
    <div className={styles.composerWrapper} id="timelineFocusHelper" onPointerDownCapture={focusTimeline}>
      {initialLoad && <FullPageLoader loading />}
      <div className={styles.composerHeader} data-drawing-toolbar-anchor onClick={disablePlayheadFollowerCallback}>
        <div className={styles.composerHeaderLeftSection}>
          <TimelineButton
            className={cx(styles.leftSectionBtn, styles.mobileControlCenterAction)}
            onContextMenu={zoomToFit}
            disabled={markMargin >= maxAutoMargin - 1e-6}
            {...zoomInHandlers}
          >
            <ZoomInIcon />
            Zoom In
          </TimelineButton>
          <TimelineButton
            className={cx(styles.leftSectionBtn, styles.mobileControlCenterAction)}
            onContextMenu={zoomToMax}
            disabled={markMargin <= minAutoMargin + 1e-6}
            {...zoomOutHandlers}
          >
            <ZoomOutIcon />
            Zoom Out
          </TimelineButton>
          <TimelineButton className={styles.leftSectionBtn} onClick={switchFullScreen}>
            <FullScreen />
            Full Screen
          </TimelineButton>
          <TimelineButton
            className={cx(styles.leftSectionBtn, styles.mobileControlCenterAction)}
            onClick={onPlayPause}
            disabled={currentComposerVideos.length === 0}
            // @ts-ignore
            ref={playButtonRef}
          >
            {composerPlayControl.composerControls.playing ? <PauseIcon style={{ width: '16px' }} /> : <PlayIcon />}
            Play
          </TimelineButton>
          {composerData && canEdit && (
              <TimelineButton
                className={styles.leftSectionBtn}
                onClick={switchShowDeleteComposerClipModal}
                disabled={!composerRows.find((t) => t.clips.length)}
              >
                <DeleteIcon />
                Delete
              </TimelineButton>
            )}

          <TimelineButton className={cx(styles.leftSectionBtn, styles.mobileControlCenterAction)}>
            <div className={styles.volumeContainer} onMouseEnter={showVolumeSlider} onMouseLeave={closeVolumeSlider}>
              {showVolume && (
                <div className={styles.volumeSlider}>
                  <Box>
                    <Stack spacing={2} sx={{ mb: 1 }} alignItems="center">
                      <Slider
                        color="secondary"
                        aria-label="Volume"
                        orientation="vertical"
                        value={composerPlayControl.composerControls.volume * 100}
                        valueLabelDisplay="auto"
                        onChange={handleVolumeChange}
                        sx={{
                          height: 20,
                          '& .MuiSlider-thumb': {
                            display: 'none',
                          },
                        }}
                      />
                    </Stack>
                  </Box>
                </div>
              )}
              <div className={styles.volumeButton}>
                {composerPlayControl.composerControls.volume > 0 ? (
                  <VolumeIcon onClick={muteSwitch} />
                ) : (
                  <MuteIcon className={styles.muteIcon} onClick={muteSwitch} />
                )}
              </div>
            </div>
            <div onClick={muteSwitch}>Volume</div>
          </TimelineButton>

          <div className={styles.composerTime} id="notes-switcher">
            <span>{timeLineFormatTime(progressPosition || 0, true)}</span>
          </div>
          {filterActive && (
            <TimelineButton
              className={styles.leftSectionBtn}
              onClick={selectFilteredClips}
              disabled={waitingForComments || !filteredClips.length}
              aria-label={`Select all ${shown} filtered clips`}
            >
              <div className={styles.clipCountNumber}>{waitingForComments ? '…' : hasClipSelection ? selectedFilteredClipCount : shown}</div>
              {hasClipSelection ? 'Selected' : 'Filtered'}
            </TimelineButton>
          )}
        </div>

        <div className={styles.rightHeaderSection}>
          <FavouriteStar toolbar />
          {showCopy && (
            <button className={styles.headerBtn} onClick={switchCopyModal}>
              <FileCopyIcon />
              <div>Copy link</div>
            </button>
          )}

          <button className={styles.headerBtn} onClick={undoStep} disabled={undoStack.length === 0 || quickLoader}>
            <UndoIcon className={undoStack.length === 0 || quickLoader ? styles.undoIconDisabled : styles.undoIconActive} />
            <div>
              Undo
              <br />
              Action
            </div>
          </button>

          {canInteractMyComposer && (
            <button className={styles.headerBtn} onClick={switchCreateRowModal}>
              <AddClipIcon />
              <div>
                Add
                <br />
                New Topic
              </div>
            </button>
          )}

          {!isMyComposer && composerData && !disableDownloadInSharedComposer && (
            <button
              className={styles.headerBtn}
              onClick={handleDownloadClick}
              onMouseEnter={() => setIsDownloadButtonHovered(true)}
              onMouseLeave={() => setIsDownloadButtonHovered(false)}
            >
              <DownloadIcon />
              <div>
                Download
                <br />
                <span style={{ opacity: 0 }}>Video</span>
              </div>
            </button>
          )}

          {organizationHaveDownloadHours &&
            isMyComposer &&
            !disableDownloadInSharedComposer &&
            !(isOwnMyComposer && !canDownloadOwnMyComposer) && (
              <button
                className={styles.headerBtn}
                onClick={openComposerDownload}
                disabled={isOwnMyComposer && !canDownloadOwnMyComposer}
                onMouseEnter={() => setIsDownloadButtonHovered(true)}
                onMouseLeave={() => setIsDownloadButtonHovered(false)}
              >
                <DownloadIcon />
                <div>
                  Download
                  <br />
                  <span style={{ opacity: 0 }}>Video</span>
                </div>
              </button>
            )}

          {canShowCreateAndSendButtons && (
            <>
              {/* @ts-ignore */}
              <button
                className={styles.headerBtn}
                onClick={switchSendClipModal}
                disabled={!canInteract || currentSelectedComposerClips.length === 0}
              >
                <SendIcon />
                <div>Send Clips</div>
              </button>
            </>
          )}

          <button className={styles.headerBtn} onClick={switchNotes}>
            {showNotes ? <ToggleOnOutlinedIcon /> : <ToggleOffOutlinedIcon />}
            <div>
              <div>
                {showNotes ? 'Hide ' : 'Show '}clips
                <br />
                Information
              </div>
            </div>
          </button>

          <FilterToolbarButton className={styles.headerBtn} />
          <div ref={speedOptionMenuRef}>
            <button className={styles.headerBtn} id="composerPlaybackRef" onClick={switchSpeedOptionMenu}>
              <div className={styles.playbackWrapper}>
                <PlaybackSpeed fontSize="small" />
                Playback
                <br />
                Speed
              </div>
            </button>
          </div>

          <div className={styles.headerBtn}>
            <VideoQualitySelector videoData={videoData} showLabel />
          </div>
        </div>
      </div>

      {clipDrawer && (
        <Draggable cancel=".no-drag">
          <div className={styles.modalContainer}>
            <div className={styles.clipDrawerModal} id="clip-drawer-modal" ref={clipDrawerRef}>
              {loading && <LinearProgress />}
              <div className={styles.modalTitle}>
                <div>Clip editing</div>
                <div ref={clipColorButtonRef} className={cx(styles.chooseColorButton, 'no-drag')} onClick={switchColorPicker}>
                  {' '}
                  <div className={styles.chosenColor} style={{ background: clipDrawer.color }} /> Choose color{' '}
                </div>
                <div className={styles.locateDrawer} onClick={onLocateDrawer}>
                  <Locate fontSize="small" />{' '}
                </div>
              </div>

              <div className={styles.modalInputs}>
                <div className={cx(styles.formattedSecondsStart, 'no-drag')}>{formatSeconds(clipDrawer.time_start)}</div>
                <input className={'no-drag'} value={clipDrawer.time_start.toFixed(0)} type="text" onChange={handleStartChange} />{' '}
                <div className={cx(styles.formattedSeconds, 'no-drag')}>
                  {clipDrawer.time_end - clipDrawer.time_start < 0 ? '00:00' : formatSeconds(clipDrawer.time_end - clipDrawer.time_start)}
                </div>
                <CenterLine className={styles.centerLine} />
                <div className={cx(styles.formattedSecondsEnd, 'no-drag')}>{formatSeconds(clipDrawer.time_end)}</div>
                <input className={'no-drag'} value={clipDrawer.time_end.toFixed(0)} type="text" onChange={handleEndChange} />
              </div>
              <div className={cx(styles.clipEditor, 'no-drag')}>
                <div className={styles.clipEditorHeader}>
                  <span>Source video</span>
                  <div className={styles.clipEditorZoom}>
                    <button
                      type="button"
                      onClick={() => setClipEditorZoom((zoom) => getClipEditorZoomStep(zoom, 'out', clipEditorMaxZoom))}
                      disabled={clipEditorZoom === 1}
                    >
                      −
                    </button>
                    <button type="button" onClick={fitClipEditorToClip} title="Fit clip">
                      Fit
                    </button>
                    <button
                      type="button"
                      onClick={() => setClipEditorZoom((zoom) => getClipEditorZoomStep(zoom, 'in', clipEditorMaxZoom))}
                      disabled={clipEditorZoom >= clipEditorMaxZoom}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div
                  ref={clipEditorViewportRef}
                  className={cx(styles.clipEditorViewport, dragging === 'center' && styles.clipEditorViewportLocked)}
                  onScroll={(event) => {
                    if (clipEditorDragViewportRef.current != null) {
                      if (event.currentTarget.scrollLeft !== clipEditorDragViewportRef.current) {
                        event.currentTarget.scrollLeft = clipEditorDragViewportRef.current;
                      }
                      return;
                    }
                    setClipEditorScrollLeft(event.currentTarget.scrollLeft);
                  }}
                >
                  <div
                    ref={clipEditorTrackRef}
                    className={styles.clipEditorTrack}
                    style={{ width: `${clipEditorZoom * 100}%` }}
                    onClick={handleClipEditorClick}
                  >
                    {clipEditorTimestamps.map((timestamp, index) => (
                      <div
                        key={timestamp.left}
                        className={cx(
                          styles.clipEditorTimestamp,
                          index === clipEditorTimestamps.length - 1 && styles.lastClipEditorTimestamp,
                        )}
                        style={{ left: `${timestamp.left}%` }}
                      >
                        <span>{timeLineFormatTime(timestamp.time)}</span>
                      </div>
                    ))}
                    <div
                      className={styles.clipEditorPlayhead}
                      style={{ left: `${(clipEditorPlayhead / clipEditorSourceDuration) * 100}%` }}
                    >
                      <div
                        className={styles.clipEditorPlayheadHandle}
                        onMouseDown={handleClipPlayheadDragStart}
                        onClick={(event) => event.stopPropagation()}
                      />
                    </div>
                    <div
                      className={styles.clipEditorSelection}
                      style={{
                        left: `${clipEditorStart}%`,
                        width: `${clipEditorWidth}%`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className={styles.clipEditorDragBody}
                        style={{ background: clipDrawer.color }}
                        onMouseDown={(e) => handleClipResizeStart(e, 'center')}
                      />
                      <div
                        className={cx(styles.clipResizeHandle, styles.leftClipResizeHandle)}
                        onMouseDown={(e) => handleClipResizeStart(e, 'left')}
                      >
                        <span>{timeLineFormatTime(clipDrawer.time_start)}</span>
                      </div>
                      <div
                        className={cx(styles.clipResizeHandle, styles.rightClipResizeHandle)}
                        onMouseDown={(e) => handleClipResizeStart(e, 'right')}
                      >
                        <span>{timeLineFormatTime(clipDrawer.time_end)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.clipEditorTimes}>
                  <span>
                    Clip: {timeLineFormatTime(clipDrawer.time_start)} — {timeLineFormatTime(clipDrawer.time_end)}
                  </span>
                  <span>Video: 0:00 — {timeLineFormatTime(clipEditorSourceDuration)}</span>
                </div>
              </div>
              {canTrimClipToPlayhead && (
                <div className={cx(styles.trimToPlayheadButtons, 'no-drag')}>
                  <OutlinedButton onClick={() => setClipBoundaryToPlayhead('in')}>
                    Set In to Playhead <span className={styles.trimShortcut}>I</span>
                  </OutlinedButton>
                  <OutlinedButton onClick={() => setClipBoundaryToPlayhead('out')}>
                    Set Out to Playhead <span className={styles.trimShortcut}>O</span>
                  </OutlinedButton>
                </div>
              )}
              {hasOriginalClipBounds && (
                <div className={cx(styles.trimToPlayheadButtons, 'no-drag')}>
                  <OutlinedButton
                    onClick={() => restoreOriginalClipBoundary('in')}
                    disabled={clipDrawer.time_start === clipDrawer.original_time_start}
                  >
                    Restore Original In
                  </OutlinedButton>
                  <OutlinedButton
                    onClick={() => restoreOriginalClipBoundary('out')}
                    disabled={clipDrawer.time_end === clipDrawer.original_time_end}
                  >
                    Restore Original Out
                  </OutlinedButton>
                </div>
              )}
              <Popper
                open={showColorPicker}
                anchorEl={clipColorButtonRef.current}
                placement="bottom-end"
                className={styles.colorPickerContainer}
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                data-preserve-clip-edit
                role="dialog"
                aria-label="Choose clip colour"
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
              >
                {showColorPicker && (
                  <div className={cx(styles.colorPicker, 'no-drag')}>
                    <ChromePicker
                      disableAlpha={true}
                      color={clipDrawer.color}
                      onChange={(color) => setClipDrawer({ ...clipDrawer, color: color.hex })}
                    />
                    <div className={styles.colorPickerModalButton}>
                      <OutlinedButton
                        className={styles.btn}
                        onClick={() => {
                          setClipDrawer({ ...clipDrawer, color: initialClipDrawer.color || '#7C7C72' });
                          switchColorPicker();
                        }}
                      >
                        {' '}
                        Cancel{' '}
                      </OutlinedButton>
                      <ContainedButton className={styles.btn} onClick={switchColorPicker}>
                        Save
                      </ContainedButton>
                    </div>
                  </div>
                )}
              </Popper>
              <div className={styles.modalButtons}>
                <div onClick={onCancelDrawerClick} className={styles.resetClipButton}>
                  Reset{' '}
                </div>
                <div className={styles.actionButtons}>
                  <OutlinedButton disabled={loading} onClick={cancelClipEditing} kind="default">
                    Cancel
                  </OutlinedButton>
                  <ContainedButton onClick={onClipCreate} disabled={loading}>
                    Save
                  </ContainedButton>
                </div>
              </div>
            </div>
          </div>
        </Draggable>
      )}
      {quickLoader && <LinearProgress />}
      {loading && <LinearProgress />}
      <FilterSummary />
      <div
        id="composer_parent_scroller"
        ref={composerRef}
        className={cx(styles.scrollWrapper, clipDrawer?.isEdit && styles.scrollWrapperLocked)}
        style={{ height: `${videoPlayerHeight}px - 62px` }}
        onMouseMove={handleManualComposerMouseMove}
        onWheel={disablePlayheadFollowing}
        onTouchMove={disablePlayheadFollowing}
      >
        {composerRows.length > 0 && !loading && (
          <DragDropContext
            onDragStart={() => {
              document.body.style.pointerEvents = 'none';
              setIsDraggingClip(true);
            }}
            onDragEnd={(result: any) => {
              document.body.style.pointerEvents = 'auto';
              setIsDraggingClip(false);
              onDrop(result);
            }}
          >
            <Droppable
              renderClone={(provided: any, snapshot: any, rubric: any) => {
                const topic = composerRows[rubric.source.index];
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={styles.minRow}
                    style={{
                      ...provided.draggableProps.style,
                      height: '65px', // force 40px tall
                      overflow: 'hidden',
                      width: '270px',
                      background: '#183047',
                      color: 'white',
                    }}
                  >
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div
                        style={{
                          textAlign: 'center',
                          background: '#525252',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '20px',
                          height: '65px',
                        }}
                      >
                        {rubric.source.index + 1}{' '}
                      </div>

                      <div style={{ padding: '4px' }}>
                        <div style={{ marginBottom: '8px' }}> {topic.name}</div>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', position: 'relative' }}>
                          {topic.clips.map((c, i) => {
                            const clipColor = c.color
                              ? `rgb(${c.color
                                  .split(',')
                                  .map((c) => +c * 256)
                                  .join(',')})`
                              : '';

                            return (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: `${5 + (i + 3) + 2}px`,
                                  bottom: `${-15 - (i + 3) - 2}px`,
                                  height: '17px',
                                  borderRadius: '2px',
                                  background: clipColor,
                                  width: '200px',
                                  zIndex: `-${i}`,
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
              droppableId="all-column"
              type={DROP_TYPE.TOPIC}
              direction="vertical"
            >
              {(provided: DroppableProvided) => (
                <div id="composerRows" className={styles.composerRows} {...provided.droppableProps} ref={provided.innerRef}>
                  {composerRows.map((topic, rowIndex) => (
                    <ComposerRow
                      collapsed={collapsedRowIds.has(String(topic.id)) && !topic.clips.some((clip) => clip.id === clipDrawer?.id)}
                      dimmedIds={dimmedIds}
                      canDragRow={canDragRow}
                      canDragClip={canDragClip}
                      key={topic.id}
                      handleEditClip={handleEditClip}
                      clipDrawer={clipDrawer}
                      topic={topic}
                      rowIndex={rowIndex}
                      selectClip={selectClip}
                      handleEditSubmit={handleEditSubmit}
                      switchShowDeleteComposerClipModal={switchShowDeleteComposerClipModal}
                      setUndoStack={setUndoStack}
                      clipUndoMapRef={clipUndoMapRef}
                      renameUndoMapRef={renameUndoMapRef}
                      switchAdditionalDeleteComposerModal={switchAdditionalDeleteComposerModal}
                      isCollaborator={!!isCollaborator}
                      isDraggingClip={isDraggingClip}
                      playheadVisible={playheadVisible}
                      switchSendClipModal={openSendFromSelection}
                      beforeMutatingComposer={ensureGeneratedMoviesWarningBeforeMutation}
                      generatedClipIdSet={generatedClipIdSet}
                      showGeneratedDownloadHints={isDownloadButtonHovered}
                    />
                  ))}
                  {provided.placeholder}
                  {isMobile && isMobileControlCenterOpen && <div className={styles.mobileControlCenterSpacer} />}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
      {isMobile && (
        <MobileControlCenter
          currentTime={progressPosition}
          duration={composerPlayControl.composerVideoLength}
          playing={composerPlayControl.composerControls.playing}
          muted={composerPlayControl.composerControls.volume <= 0}
          selectedCount={currentSelectedComposerClips.length + currentSelectedComposerRows.length}
          onPlayPause={onPlayPause}
          onMuteToggle={muteSwitch}
          onPreviousClip={handlePreviousComposerClip}
          onNextClip={handleNextComposerClip}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onSeek={handleMobileComposerSeek}
          onDeselect={handleMobileComposerDeselect}
          onLocatePlayhead={toggleMobileFollowPlayhead}
          onShowComments={onShowComments}
          onOpenChange={setIsMobileControlCenterOpen}
          canSwitchAngle={(composerVideoClips?.length || currentComposerVideos.length) > 1}
          followPlayhead={followPlayhead}
          zoomInDisabled={markMargin >= maxAutoMargin - 1e-6}
          zoomOutDisabled={markMargin <= minAutoMargin + 1e-6}
        />
      )}
      <SendClipModal
        setSkipConfirmationModals={setSkipConfirmationModals}
        skipConfirmation={skipConfirmationModals}
        open={showSendClipModal}
        switchModal={switchSendClipModal}
        onCreateComposer={switchNewComposerModal}
        beforeSendToComposer={(targetFeedId) => ensureGeneratedMoviesWarningBeforeMutation(targetFeedId)}
        showCurrentClipChoiceWhenMultipleSelected={true}
        canCreateComposer={canCreateComposerFromContent}
      />
      <NewComposerModal
        setSkipConfirmationModals={setSkipConfirmationModals}
        skipConfirmation={skipConfirmationModals}
        open={showNewComposerModal}
        switchModal={switchNewComposerModal}
      />
      {composerData &&
        currentUser?.id !== composerData?.created_by?.id &&
        composerData?.type !== VIDEOFEED_TYPE.MY_COMPOSER &&
        canCreateComposerFromContent && (
          <CreateOrSendModal
            open={showCreateOrSendModal}
            switchModal={switchCreateOrSendModal}
            switchSendClipModal={switchSendClipModal}
            switchNewComposerModal={switchNewComposerModal}
            setSkipConfirmationModals={setSkipConfirmationModals}
            canCreateComposer={canCreateComposerFromContent}
          />
        )}

      {composerData && currentUser?.id === composerData?.created_by?.id && composerData?.type !== VIDEOFEED_TYPE.MY_COMPOSER && (
        <CreateOrSendModal
          open={showCreateOrSendModal}
          switchModal={switchCreateOrSendModal}
          switchSendClipModal={switchSendClipModal}
          switchNewComposerModal={switchNewComposerModal}
          setSkipConfirmationModals={setSkipConfirmationModals}
          canCreateComposer={canCreateComposerFromContent}
        />
      )}

      {composerData && canEdit && (
          <DeleteComposerClipModal
            switchAdditionalDeleteComposerModal={switchAdditionalDeleteComposerModal}
            switchModal={switchShowDeleteComposerClipModal}
            open={showDeleteClipModal}
            setUndoStack={setUndoStack}
            clipUndoMapRef={clipUndoMapRef}
            beforeMutatingComposer={ensureGeneratedMoviesWarningBeforeMutation}
          />
        )}
      <DeleteComposerAdditional
        composerId={feedId ? feedId : 'noFeedId'}
        open={showAdditionalDeleteModal}
        switchModal={switchAdditionalDeleteComposerModal}
      />

      {composerData && (
        <CopyLinkModal
          open={showCopyModal}
          switchModal={switchCopyModal}
          feedData={composerData}
          itsComposer={true}
          composerQuality={computedQuality}
        />
      )}
      <CreateRowModal
        open={createRowModal}
        position="bottom"
        switchModal={switchCreateRowModal}
        createdNewRowCallBack={createdNewRowCallBack}
      />
      <GeneratedMoviesWarningModal
        open={showGeneratedMoviesWarningModal}
        onConfirm={() => resolveGeneratedMoviesWarningModal(true)}
        onClose={() => resolveGeneratedMoviesWarningModal(false)}
      />
      {!!composerData && <DownloadDataModal switchModal={switchDownloadDataModal} isOpen={showDownloadModal} feedId={composerData.id} />}
    </div>
  );
};

export default Composer;
